import React from "react";
import Modal from "@cloudscape-design/components/modal";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import Checkbox from "@cloudscape-design/components/checkbox";
import Container from "@cloudscape-design/components/container";
import Alert from "@cloudscape-design/components/alert";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import Textarea from "@cloudscape-design/components/textarea";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { achievementAddModalStore } from "../stores/achievementAddModal";
import Header from "@cloudscape-design/components/header";
import Grid from "@cloudscape-design/components/grid";
import DatePicker from "@cloudscape-design/components/date-picker";
import { gameStore } from "../stores/games";
import { configStore } from "../stores/config";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import { RISK_MODEL_OPTIONS, getRiskModelSelectedOptions } from "../helpers/riskModelOptions";
import { PARTNER_PAYMENT_TYPE_OPTIONS } from "../helpers/cohortConditions";

import DailyProgressiveTab, {
	INITIAL_DAILY_PROGRESSIVE_CONFIG,
	INITIAL_NEW_MILESTONE,
	formatGamesForSelect,
	getGamesByProvider,
} from "./AchievementDailyProgressiveTab";
import {
	buildAchievementTransferData,
	buildAddAchievementStateFromTransfer,
	buildCohortGroupsFromConditions,
	copyTextToClipboard,
	getAchievementTransferJson,
	parseAchievementTransferEnvelope,
	stringifyAchievementTransfer,
} from "../helpers/achievementTransferJson";
import { formatChestPrizeType } from "../helpers/chestPrizeTypes";

const parseChestRewardConditions = (value, fallback = {}) => {
	if (Array.isArray(value)) {
		return value;
	}

	if (value && typeof value === "object") {
		return value;
	}

	if (typeof value === "string" && value.trim().length) {
		try {
			return JSON.parse(value);
		} catch (error) {
			return fallback;
		}
	}

	return fallback;
};

const getChestRewardsPayload = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions, []);

	if (Array.isArray(parsed)) {
		return parsed;
	}

	if (Array.isArray(parsed?.prizes)) {
		return parsed.prizes;
	}

	return [];
};

const getChestPrizeIdFromRewardConditions = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions);

	if (Array.isArray(parsed)) {
		return null;
	}

	return parsed?.chestPrizeId || null;
};

const getChestTypeFromRewardConditions = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions);

	if (Array.isArray(parsed)) {
		return "";
	}

	return parsed?.type || "";
};

const normalizeChestPrizeValue = (value) => {
	if (Array.isArray(value)) {
		return value.map((entry) => normalizeChestPrizeValue(entry));
	}

	if (value && typeof value === "object") {
		return Object.keys(value)
			.sort()
			.reduce((accumulator, key) => {
				accumulator[key] = normalizeChestPrizeValue(value[key]);
				return accumulator;
			}, {});
	}

	return value;
};

const getChestPrizeSignature = ({ type, rewardConditions }) =>
	JSON.stringify({
		type: type || "",
		rewardConditions: normalizeChestPrizeValue(getChestRewardsPayload(rewardConditions)),
	});

const findMatchingChestPrize = (items = [], { type, rewardConditions }) => {
	const chestPrizeId = getChestPrizeIdFromRewardConditions(rewardConditions);
	if (chestPrizeId) {
		return (items || []).find((item) => Number(item.id) === Number(chestPrizeId)) || null;
	}

	const targetSignature = getChestPrizeSignature({ type, rewardConditions });

	return (
		(items || []).find(
			(item) =>
				getChestPrizeSignature({
					type: item.type,
					rewardConditions: item.rewardConditions,
				}) === targetSignature
		) || null
	);
};

const formatChestRewardsSummary = (rewardConditions, fallback = "Chest config") => {
	const rewards = getChestRewardsPayload(rewardConditions);
	if (!rewards.length) {
		return fallback;
	}

	const totalProbability = rewards.reduce(
		(sum, reward) => sum + (Number(reward.probability) || 0),
		0
	);

	return `Chest • ${rewards.length} rewards • ${totalProbability}%`;
};

const getChestPrizeDisplayLabel = (items = [], { type, rewardConditions, fallback = "Chest config" }) => {
	const match = findMatchingChestPrize(items, { type, rewardConditions });

	return match ? `${match.name} (${match.id})` : formatChestRewardsSummary(rewardConditions, fallback);
};

const buildChestPrizeOption = (item) => ({
	value: String(item.id),
	label: `${item.name} (${item.id})`,
	description: formatChestPrizeType(item.type),
	chestPrize: item,
});

const FREESPINS_PROVIDER_OPTIONS = [{ label: "Endorphina", value: "endorphina", type: 10 }];
const CHEST_REWARD_OPTIONS = [
	{ value: "money", label: "Money" },
	{ value: "tickets", label: "Tickets" },
	{ value: "freespins", label: "Freespins" },
];

const createChestRewardDraft = () => ({
	id: crypto.randomUUID(),
	rewardType: null,
	rewardValue: "",
	probability: "",
	rewardConditions: {},
	providerSelected: null,
	gameSelected: null,
	betAmount: "",
	expiresAt: "",
});

class AchievementAddModal extends React.Component {
	chestPrizeOptionsRequest = null;

	state = {
		imageError: "",
		levelError: "",
		availableLevelGames: [],
		availableLevelSpinsGames: [],
		newChestRewards: [],
		newChestType: "",
		newChestPrizeId: null,
		newChestPrizeSelected: null,
		newSpinsProvider: null,
		newSpinsGame: null,
		cohortGroups: [
			{
				groupId: crypto.randomUUID(),
				module: "deposit",
				groupKey: "depositFilters",
				groupLogicalOperator: null,
			},
		],
		newConditionByGroupId: {},
		dailyProgressiveConfig: { ...INITIAL_DAILY_PROGRESSIVE_CONFIG },
		newMilestone: { ...INITIAL_NEW_MILESTONE },
		availableMilestoneGames: [],
		dailyConditionsSpinsGames: [],
		jsonText: "",
		jsonError: "",
		jsonDirty: false,
		jsonImagePreview: "",
	};

	componentDidUpdate(prevProps) {
		const prevRewardType = prevProps.achievementAddModal.rewardType?.value;
		const currentRewardType = this.props.achievementAddModal.rewardType?.value;

		if (
			prevRewardType !== "chest" &&
			currentRewardType === "chest" &&
			!this.props.achievementAddModal.chestPrizeOptions.length
		) {
			this.loadChestPrizeOptions();
		}
	}

	getGroupKeyByModule = (module) => {
		if (module === "deposit") return "depositFilters";
		if (module === "withdrawal") return "withdrawalFilters";
		if (module === "registration") return "registrationFilters";
		return "profileFilters";
	};

	getCohortModuleOptions = () => [
		{ value: "deposit", label: "Deposit" },
		{ value: "withdrawal", label: "Withdrawal" },
		{ value: "registration", label: "Registration" },
		{ value: "profile", label: "Profile" },
	];

	getLogicalOperatorOptions = () => [
		{ value: "AND", label: "AND" },
		{ value: "OR", label: "OR" },
	];

	getCohortFieldOptions = () => [
		{ value: "id", label: "Id", type: "string" },
		{ value: "risk_model", label: "Risk model", type: "multiselect", specialType: "riskModel" },
		{ value: "verify", label: "Verify", type: "select", specialType: "boolean" },
		{ value: "sweepstakes_count", label: "Sweepstakes_count", type: "count" },
		{ value: "tasks_count", label: "Tasks count", type: "count" },
		{ value: "email", label: "Email", type: "select", specialType: "boolean" },
		{ value: "phone", label: "Phone", type: "select", specialType: "boolean" },
		{ value: "tg", label: "Telegram", type: "select", specialType: "boolean" },
		{ value: "partnerName", label: "Partner Name", type: "string" },
		{ value: "paymentType", label: "Payment Type", type: "select", specialType: "partnerPaymentType" },
		{ value: "trafficType", label: "Traffic Type", type: "string" },

		{ value: "deposit_date", label: "Deposit date", type: "count" },
		{ value: "deposit_date_crypto", label: "Deposit crypto date", type: "count" },
		{ value: "deposit_date_fiat", label: "Deposit fiat date", type: "count" },
		{ value: "deposit_count", label: "Deposit count", type: "count" },
		{ value: "deposit_count_fiat", label: "Deposit fiat count", type: "count" },
		{ value: "deposit_count_crypto", label: "Deposit crypto count", type: "count" },
		{ value: "deposit_from", label: "Deposit from", type: "date" },
		{ value: "deposit_to", label: "Deposit to", type: "date" },
		{ value: "deposit_crypto_from", label: "Deposit crypto from", type: "date" },
		{ value: "deposit_crypto_to", label: "Deposit crypto to", type: "date" },
		{ value: "deposit_fiat_from", label: "Deposit fiat from", type: "date" },
		{ value: "deposit_fiat_to", label: "Deposit fiat to", type: "date" },
		{ value: "deposit_amount_to", label: "Deposit amount to", type: "count" },
		{ value: "deposit_amount_from", label: "Deposit amount from", type: "count" },
		{ value: "deposit_amount_crypto_to", label: "Deposit amount crypto to", type: "count" },
		{ value: "deposit_amount_crypto_from", label: "Deposit amount crypto from", type: "count" },
		{ value: "deposit_amount_fiat_to", label: "Deposit amount fiat to", type: "count" },
		{ value: "deposit_amount_fiat_from", label: "Deposit amount fiat from", type: "count" },
		{ value: "deposit_type", label: "Deposit type", type: "select", specialType: "cryptoOrFiat" },

		{ value: "withdrawal_date", label: "Withdrawal date", type: "count" },
		{ value: "withdrawal_date_crypto", label: "Withdrawal crypto date", type: "count" },
		{ value: "withdrawal_date_fiat", label: "Withdrawal fiat date", type: "count" },
		{ value: "withdrawal_count", label: "Withdrawal count", type: "count" },
		{ value: "withdrawal_count_fiat", label: "Withdrawal fiat count", type: "count" },
		{ value: "withdrawal_count_crypto", label: "Withdrawal crypto count", type: "count" },
		{ value: "withdrawal_from", label: "Withdrawal from", type: "date" },
		{ value: "withdrawal_to", label: "Withdrawal to", type: "date" },
		{ value: "withdrawal_crypto_from", label: "Withdrawal crypto from", type: "date" },
		{ value: "withdrawal_crypto_to", label: "Withdrawal crypto to", type: "date" },
		{ value: "withdrawal_fiat_from", label: "Withdrawal fiat from", type: "date" },
		{ value: "withdrawal_fiat_to", label: "Withdrawal fiat to", type: "date" },
		{ value: "withdrawal_amount_to", label: "Withdrawal amount to", type: "count" },
		{ value: "withdrawal_amount_from", label: "Withdrawal amount from", type: "count" },
		{ value: "withdrawal_amount_crypto_to", label: "Withdrawal amount crypto to", type: "count" },
		{ value: "withdrawal_amount_crypto_from", label: "Withdrawal amount crypto from", type: "count" },
		{ value: "withdrawal_amount_fiat_to", label: "Withdrawal amount fiat to", type: "count" },
		{ value: "withdrawal_amount_fiat_from", label: "Withdrawal amount fiat from", type: "count" },
		{ value: "withdrawal_type", label: "Withdrawal type", type: "select", specialType: "cryptoOrFiat" },

		{ value: "spins", label: "Spins", type: "count" },
		{ value: "lvl_club", label: "Club", type: "count" },
		{ value: "referer", label: "Referer", type: "count" },
		{ value: "balance", label: "Balance", type: "count" },
		{ value: "registration_from", label: "Registration from", type: "date" },
		{ value: "registration_to", label: "Registration to", type: "date" },
		{ value: "registration_date", label: "Registration user days", type: "count" },
	];

	getCohortFieldOptionsByModule = (module) => {
		const fields = this.getCohortFieldOptions();

		if (module === "deposit") return fields.filter((f) => f.value.startsWith("deposit_"));
		if (module === "withdrawal") return fields.filter((f) => f.value.startsWith("withdrawal_"));
		if (module === "registration") return fields.filter((f) => f.value.startsWith("registration_"));

		return fields.filter((f) => !f.value.startsWith("deposit_") && !f.value.startsWith("withdrawal_") && !f.value.startsWith("registration_"));
	};

	updateNewCondition = (groupId, patch) => {
		this.setState((prev) => ({
			newConditionByGroupId: {
				...(prev.newConditionByGroupId || {}),
				[groupId]: {
					...(prev.newConditionByGroupId?.[groupId] || {}),
					...patch,
				},
			},
		}));
	};

	resetNewCondition = (groupId) => {
		this.setState((prev) => ({
			newConditionByGroupId: {
				...(prev.newConditionByGroupId || {}),
				[groupId]: {},
			},
		}));
	};

	isConditionValueEmpty = (value) => value === null || value === "" || (Array.isArray(value) && value.length === 0);

	normalizeGroupConditions = (conditions, groupId) => {
		const groupConditions = (conditions || []).filter((c) => c.groupId === groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

		if (!groupConditions.length) return conditions;

		const firstId = groupConditions[0].id;
		return (conditions || []).map((c) => {
			if (c.groupId !== groupId) return c;
			if (c.id === firstId) return { ...c, logicalOperator: null };
			if (!c.logicalOperator) return { ...c, logicalOperator: "AND" };
			return c;
		});
	};

	addCohortGroup = () => {
		const groupId = crypto.randomUUID();

		this.setState((prev) => {
			const groups = [...(prev.cohortGroups || [])];
			const groupLogicalOperator = groups.length === 0 ? null : "AND";

			return {
				cohortGroups: [
					...groups,
					{
						groupId,
						module: "deposit",
						groupKey: "depositFilters",
						groupLogicalOperator,
					},
				],
			};
		});
	};

	updateCohortGroup = (groupId, patch) => {
		const nextGroups = (this.state.cohortGroups || []).map((g) => (g.groupId === groupId ? { ...g, ...patch } : g));

		this.setState({ cohortGroups: nextGroups });

		const conditions = this.props.achievementAddModal.conditions || [];
		const updatedConditions = conditions.map((c) =>
			c.groupId === groupId
				? {
						...c,
						...(typeof patch.module !== "undefined" ? { module: patch.module } : {}),
						...(typeof patch.module !== "undefined" ? { groupKey: this.getGroupKeyByModule(patch.module) } : {}),
						...(typeof patch.groupLogicalOperator !== "undefined" ? { groupLogicalOperator: patch.groupLogicalOperator } : {}),
				  }
				: c,
		);

		this.props.setAchievementAddModal({
			...this.props.achievementAddModal,
			conditions: updatedConditions,
		});
	};

	changeCohortGroupModule = (groupId, module) => {
		const nextGroups = (this.state.cohortGroups || []).map((g) => (g.groupId === groupId ? { ...g, module, groupKey: this.getGroupKeyByModule(module) } : g));

		this.setState({ cohortGroups: nextGroups });

		const nextConditions = (this.props.achievementAddModal.conditions || []).filter((c) => c.groupId !== groupId);

		this.props.setAchievementAddModal({
			...this.props.achievementAddModal,
			conditions: nextConditions,
		});

		this.resetNewCondition(groupId);
	};

	removeCohortGroup = (groupId) => {
		const nextGroups = (this.state.cohortGroups || [])
			.filter((g) => g.groupId !== groupId)
			.map((g, idx) => ({
				...g,
				groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
			}));

		const groupIndexById = new Map(nextGroups.map((g, idx) => [g.groupId, idx]));

		const nextConditions = (this.props.achievementAddModal.conditions || [])
			.filter((c) => c.groupId !== groupId)
			.filter((c) => groupIndexById.has(c.groupId))
			.map((c) => {
				const groupOrder = groupIndexById.get(c.groupId);
				const group = nextGroups[groupOrder];

				return {
					...c,
					module: group.module,
					groupKey: group.groupKey || this.getGroupKeyByModule(group.module),
					groupOrder,
					groupLogicalOperator: groupOrder === 0 ? null : group.groupLogicalOperator || "AND",
				};
			});

		this.setState((prev) => {
			const nextNewConditionByGroupId = { ...(prev.newConditionByGroupId || {}) };
			delete nextNewConditionByGroupId[groupId];

			return {
				cohortGroups: nextGroups,
				newConditionByGroupId: nextNewConditionByGroupId,
			};
		});

		this.props.setAchievementAddModal({
			...this.props.achievementAddModal,
			conditions: nextConditions,
		});
	};

	addCohortCondition = (group, groupIndex) => {
		const conditions = this.props.achievementAddModal.conditions || [];
		const groupConditions = conditions.filter((c) => c.groupId === group.groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

		const newCondition = this.state.newConditionByGroupId?.[group.groupId] || {};
		if (!newCondition.field || this.isConditionValueEmpty(newCondition.value)) return;

		let value = newCondition.value;
		if (newCondition.conditionalType === "select") {
			value = newCondition.value.value || newCondition.value;
		} else if (newCondition.conditionalType === "boolean") {
			value = newCondition.value ? 1 : 0;
		}

		const nextOrder = groupConditions.length ? Math.max(...groupConditions.map((c) => c.conditionOrder ?? 0)) + 1 : 0;

		const logicalOperator = groupConditions.length ? newCondition.logicalOperator || "AND" : null;

		const item = {
			...newCondition,
			id: crypto.randomUUID(),
			value,
			logicalOperator,
			conditionOrder: nextOrder,
			groupId: group.groupId,
			module: group.module,
			groupKey: group.groupKey || this.getGroupKeyByModule(group.module),
			groupOrder: groupIndex,
			groupLogicalOperator: groupIndex === 0 ? null : group.groupLogicalOperator || "AND",
		};

		const nextConditions = this.normalizeGroupConditions([...conditions, item], group.groupId);

		this.props.setAchievementAddModal({
			...this.props.achievementAddModal,
			conditions: nextConditions,
		});
		this.resetNewCondition(group.groupId);
	};

	componentWillUnmount() {
		if (this.props.achievementAddModal.image) {
			URL.revokeObjectURL(this.props.achievementAddModal.image);
		}
	}

	renderComponentFromType(groupId, type, specialType) {
		//count boolean date string

		const newCondition = this.state.newConditionByGroupId?.[groupId] || {};

		if (type === "multiselect" && specialType === "riskModel") {
			return <Multiselect selectedOptions={getRiskModelSelectedOptions(newCondition?.value)} onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOptions.map((option) => option.value) })} options={RISK_MODEL_OPTIONS} placeholder="Select risk models" selectedAriaLabel="Selected" />;
		} else if (type === "string") {
			return <Input placeholder="Enter value" value={newCondition?.value || ""} onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value })} />;
		} else if (type === "count") {
			return <Input placeholder="Enter value" type="number" value={newCondition?.value || ""} onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value })} />;
		} else if (type === "date") {
			return (
				<DatePicker
					onChange={({ detail }) => {
						this.updateNewCondition(groupId, { value: detail.value });
					}}
					value={newCondition?.value || null}
					placeholder="Select a date"
				/>
			);
		} else if (type === "select" && specialType === "boolean") {
			return (
				<Select
					selectedOption={newCondition?.value}
					onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOption })}
					options={[
						{ value: "1", label: "Required" },
						{ value: "0", label: "No" },
					]}
					placeholder="Select type"
				/>
			);
		} else if (type === "select" && specialType === "cryptoOrFiat") {
			return (
				<Select
					selectedOption={newCondition?.value}
					onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOption })}
					options={[
						{ value: "crypto", label: "Crypto" },
						{ value: "fiat", label: "Fiat" },
					]}
					placeholder="Select type"
				/>
			);
		} else if (type === "select" && specialType === "partnerPaymentType") {
			return (
				<Select
					selectedOption={newCondition?.value}
					onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOption })}
					options={PARTNER_PAYMENT_TYPE_OPTIONS}
					placeholder="Select payment type"
				/>
			);
		}

		return null;
	}

	onDismiss = () => {
		this.props.setAchievementAddModal({
			...this.props.achievementAddModal,
			name: "",
			description: "",
			type: "",
			rewardValue: "",
			image: null,
			cycle: null,
			cycleUnit: null,
			cycleTimer: null,
			buttonText: "",
			link: "",
			active: "",
			categories: [],
			count: "",
			loaded: true,
			startDate: null,
			endDate: null,
			show: false,
			providerSpinsSelected: null,
			gameSpinsSelected: null,
			availableSpinsGames: [],
			availableFreespinsGames: [],
			rewardType: "",
			availableGames: [],
			chestRewards: [],
			chestType: "",
			chestPrizeId: null,
			chestPrizeSelected: null,
			chestPrizeOptions: [],
			chestPrizeLoading: false,
			chestPrizeMatchMissing: false,
			gameSpinsAmount: 0,
			nameTranslationType: "",
			descriptionTranslationType: "",
			buttonTranslationType: "",
			betAmount: "",
			expiresAt: "",
		});
		this.setState({
			newChestRewards: [],
			newChestType: "",
			newChestPrizeId: null,
			newChestPrizeSelected: null,
			newMilestone: { ...INITIAL_NEW_MILESTONE },
			jsonText: "",
			jsonError: "",
			jsonDirty: false,
			jsonImagePreview: "",
		});
	};

	loadAvailableSpinsGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const filteredGames = gameState.data.filter((game) => {
			return game.providerID === provider && game.active === 1;
		});

		const formattedGames = filteredGames.map((game) => ({
			id: game.gameID,
			description: game.name,
			name: game.name,
			gameID: game.gameID,
			providerID: game.providerID,
		}));

		this.props.setAchievementAddModal((prevState) => ({
			...prevState,
			availableSpinsGames: formattedGames,
		}));
	};

	loadAvailableFreespinsGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const filteredGames = gameState.data.filter((game) => {
			return game.providerID === provider && game.active === 1;
		});

		const formattedGames = filteredGames.map((game) => ({
			id: game.gameID,
			description: game.name,
			name: game.name,
			gameID: game.gameID,
			providerID: game.providerID,
		}));

		this.props.setAchievementAddModal((prevState) => ({
			...prevState,
			availableFreespinsGames: formattedGames,
		}));
	};

	getAvailableEndorphinaGames = () => {
		const gameState = this.props.game;
		if (!gameState?.loaded || !gameState?.data) {
			return [];
		}

		return gameState.data
			.filter((game) => game.providerID === 10 && game.active === 1)
			.map((game) => ({
				id: game.gameID,
				description: game.name,
				name: game.name,
				gameID: game.gameID,
				providerID: game.providerID,
			}));
	};

	getChestRewardGames = () =>
		this.getAvailableEndorphinaGames().map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));

	buildFreespinRewardConditions = ({ gameSelected, betAmount, expiresAt }) => {
		const parsedBetAmount = parseFloat(betAmount) || 0;

		return {
			gameId: gameSelected?.gameId,
			providerId: gameSelected?.game?.providerID,
			betAmount: parsedBetAmount,
			currency: "RUB",
			expiresAt: parseInt(expiresAt, 10),
		};
	};

	buildChestRewardsPayload = (items = this.props.achievementAddModal.chestRewards || []) =>
		(items || []).map((item) => {
			const rewardType = item.rewardType?.value || item.rewardType;

			return {
				rewardType,
				rewardValue: parseInt(item.rewardValue, 10) || 0,
				probability: Number(item.probability),
				rewardConditions:
					rewardType === "freespins"
						? this.buildFreespinRewardConditions(item)
						: item.rewardConditions || {},
			};
		});

	loadChestPrizeOptions = async () => {
		if (this.chestPrizeOptionsRequest) {
			return this.chestPrizeOptionsRequest;
		}

		this.chestPrizeOptionsRequest = this.loadChestPrizeOptionsRequest();

		try {
			return await this.chestPrizeOptionsRequest;
		} finally {
			this.chestPrizeOptionsRequest = null;
		}
	};

	loadChestPrizeOptionsRequest = async () => {
		try {
			this.props.setAchievementAddModal((prev) => ({
				...prev,
				chestPrizeLoading: true,
			}));

			const response = await this.props.Interface.getChestPrizeOptions({ page: 1, limit: 999 });
			const items = response?.items || [];
			const options = items.map((item) => buildChestPrizeOption(item));

			this.props.setAchievementAddModal((prev) => ({
				...prev,
				chestPrizeOptions: options,
				...(prev.chestPrizeId
					? (() => {
							const match = items.find((item) => Number(item.id) === Number(prev.chestPrizeId));
							return match
								? {
										chestPrizeSelected: buildChestPrizeOption(match),
										chestType: match.type || "",
										chestRewards: getChestRewardsPayload(match.rewardConditions),
										chestPrizeMatchMissing: false,
								  }
								: {
										chestPrizeSelected: null,
										chestPrizeMatchMissing: true,
								  };
					  })()
					: {}),
				chestPrizeLoading: false,
			}));

			return { items, options };
		} catch (error) {
			console.error(error);
			this.props.setAchievementAddModal((prev) => ({
				...prev,
				chestPrizeLoading: false,
			}));
			this.props.Interface.addNoty("Error", "Failed to load chest presets");
			return { items: [], options: [] };
		}
	};

	selectChestPrize = (selectedOption) => {
		const chestPrize = selectedOption?.chestPrize || null;

		this.props.setAchievementAddModal({
			...this.props.achievementAddModal,
			chestPrizeSelected: selectedOption,
			chestPrizeId: chestPrize?.id || null,
			chestType: chestPrize?.type || "",
			chestRewards: getChestRewardsPayload(chestPrize?.rewardConditions),
			chestPrizeMatchMissing: false,
		});
	};

	selectNewLevelChestPrize = (selectedOption) => {
		const chestPrize = selectedOption?.chestPrize || null;

		this.setState({
			newChestPrizeSelected: selectedOption,
			newChestPrizeId: chestPrize?.id || null,
			newChestType: chestPrize?.type || "",
			newChestRewards: getChestRewardsPayload(chestPrize?.rewardConditions),
			levelError: "",
		});
	};

	selectDailyMilestoneChestPrize = (selectedOption) => {
		const chestPrize = selectedOption?.chestPrize || null;

		this.setState((prev) => ({
			newMilestone: {
				...prev.newMilestone,
				chestPrizeSelected: selectedOption,
				chestPrizeId: chestPrize?.id || null,
				chestType: chestPrize?.type || "",
				chestRewards: getChestRewardsPayload(chestPrize?.rewardConditions),
			},
		}));
	};

	validateSelectedChest = ({ chestPrizeId }) => {
		if (!chestPrizeId) {
			return "Chest preset is required";
		}

		return "";
	};

	validateChestRewards = (chestRewards = []) => {
		if (!chestRewards.length) {
			return "Chest rewards are required";
		}

		for (let index = 0; index < chestRewards.length; index += 1) {
			const reward = chestRewards[index];
			const rewardLabel = `Chest reward #${index + 1}`;
			const rewardType = reward.rewardType?.value || reward.rewardType;

			if (!rewardType) {
				return `${rewardLabel}: reward type is required`;
			}

			const rewardValue = parseInt(reward.rewardValue, 10);
			if (!reward.rewardValue || rewardValue < 1) {
				return `${rewardLabel}: reward value is required`;
			}

			const probability = Number(reward.probability || 0);
			if (Number.isNaN(probability) || probability < 0) {
				return `${rewardLabel}: probability must not be negative`;
			}

			if (rewardType === "freespins") {
				if (!reward.providerSelected) return `${rewardLabel}: provider is required`;
				if (!reward.gameSelected) return `${rewardLabel}: game is required`;
				if (!reward.betAmount) return `${rewardLabel}: bet amount is required`;
				if (!reward.expiresAt) return `${rewardLabel}: expiresAt is required`;

				const betAmount = parseFloat(reward.betAmount) || 0;
				if (betAmount <= 0) return `${rewardLabel}: bet amount must be greater than 0`;
			}
		}

		return "";
	};

	getChestRewardsSummary = (rewardConditions) => {
		const prizes = rewardConditions?.prizes || [];
		if (!prizes.length) {
			return "Chest config";
		}

		const totalProbability = prizes.reduce(
			(sum, reward) => sum + (Number(reward.probability) || 0),
			0
		);

		return `Chest • ${prizes.length} rewards • ${totalProbability}%`;
	};

	getChestPrizeItems = () =>
		(this.props.achievementAddModal.chestPrizeOptions || [])
			.map((option) => option.chestPrize)
			.filter(Boolean);

	getChestDisplayLabel = (rewardConditions) =>
		getChestPrizeDisplayLabel(this.getChestPrizeItems(), {
			type: getChestTypeFromRewardConditions(rewardConditions),
			rewardConditions,
			fallback: this.getChestRewardsSummary(rewardConditions),
		});

	addChestReward = () => {
		this.props.setAchievementAddModal((prev) => ({
			...prev,
			chestRewards: [...(prev.chestRewards || []), createChestRewardDraft()],
		}));
	};

	removeChestReward = (rewardId) => {
		this.props.setAchievementAddModal((prev) => ({
			...prev,
			chestRewards: (prev.chestRewards || []).filter((item) => item.id !== rewardId),
		}));
	};

	updateChestReward = (rewardId, patch) => {
		this.props.setAchievementAddModal((prev) => ({
			...prev,
			chestRewards: (prev.chestRewards || []).map((item) =>
				item.id === rewardId ? { ...item, ...patch } : item
			),
		}));
	};

	handleChestRewardTypeChange = (rewardId, rewardType) => {
		this.updateChestReward(rewardId, {
			rewardType,
			rewardValue: "",
			probability: "",
			rewardConditions: {},
			providerSelected: null,
			gameSelected: null,
			betAmount: "",
			expiresAt: "",
		});
	};

	addNewLevelChestReward = () => {
		this.setState((prev) => ({
			newChestRewards: [...(prev.newChestRewards || []), createChestRewardDraft()],
		}));
	};

	removeNewLevelChestReward = (rewardId) => {
		this.setState((prev) => ({
			newChestRewards: (prev.newChestRewards || []).filter((item) => item.id !== rewardId),
		}));
	};

	updateNewLevelChestReward = (rewardId, patch) => {
		this.setState((prev) => ({
			newChestRewards: (prev.newChestRewards || []).map((item) =>
				item.id === rewardId ? { ...item, ...patch } : item
			),
		}));
	};

	handleNewLevelChestRewardTypeChange = (rewardId, rewardType) => {
		this.updateNewLevelChestReward(rewardId, {
			rewardType,
			rewardValue: "",
			probability: "",
			rewardConditions: {},
			providerSelected: null,
			gameSelected: null,
			betAmount: "",
			expiresAt: "",
		});
	};

	loadAvailableLevelGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const filteredGames = gameState.data.filter((game) => {
			return game.providerID === provider && game.active === 1;
		});

		const formattedGames = filteredGames.map((game) => ({
			id: game.gameID,
			description: game.name,
			name: game.name,
			gameID: game.gameID,
			providerID: game.providerID,
		}));

		this.setState({ availableLevelGames: formattedGames });
	};

	loadAvailableLevelSpinsGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const filteredGames = gameState.data.filter((game) => {
			return game.providerID === provider && game.active === 1;
		});

		const formattedGames = filteredGames.map((game) => ({
			id: game.gameID,
			description: game.name,
			name: game.name,
			gameID: game.gameID,
			providerID: game.providerID,
		}));

		this.setState({ availableLevelSpinsGames: formattedGames });
	};

	updateDailyProgressiveConfig = (patch) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				...patch,
			},
		}));
	};

	updateDailyConditions = (patch) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				dailyConditions: {
					...prev.dailyProgressiveConfig.dailyConditions,
					...patch,
				},
			},
		}));
	};

	updateDailyConditionsDeposit = (patch) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				dailyConditions: {
					...prev.dailyProgressiveConfig.dailyConditions,
					deposit: {
						...prev.dailyProgressiveConfig.dailyConditions.deposit,
						...patch,
					},
				},
			},
		}));
	};

	updateDailyConditionsSpins = (patch) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				dailyConditions: {
					...prev.dailyProgressiveConfig.dailyConditions,
					spins: {
						...prev.dailyProgressiveConfig.dailyConditions.spins,
						...patch,
					},
				},
			},
		}));
	};

	loadAvailableMilestoneGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const formattedGames = getGamesByProvider(gameState.data, provider);
		this.setState({ availableMilestoneGames: formattedGames });
	};

	getFilteredMilestoneGames = () => {
		return formatGamesForSelect(this.state.availableMilestoneGames || []);
	};

	addMilestone = () => {
		const { newMilestone, dailyProgressiveConfig } = this.state;

		if (!newMilestone.day || !newMilestone.rewardType) {
			this.props.Interface.addNoty("Error", "Day and reward type are required");
			return;
		}

		if (newMilestone.rewardType.value !== "chest" && (newMilestone.rewardValue === "" || newMilestone.rewardValue === null)) {
			this.props.Interface.addNoty("Error", "Reward value is required");
			return;
		}

		const milestone = {
			id: crypto.randomUUID(),
			day: parseInt(newMilestone.day, 10),
			rewardType: newMilestone.rewardType.value,
			rewardValue: newMilestone.rewardType.value === "chest" ? 1 : parseInt(newMilestone.rewardValue, 10) || 0,
			rewardConditions: {},
		};

		if (newMilestone.rewardType.value === "freespins") {
			if (!newMilestone.freespinsProvider || !newMilestone.freespinsGame) {
				this.props.Interface.addNoty("Error", "Provider and game are required for freespins");
				return;
			}
			milestone.rewardConditions = {
				gameId: newMilestone.freespinsGame.gameId,
				providerId: newMilestone.freespinsGame.game.providerID,
				betAmount: parseInt(newMilestone.freespinsBetAmount, 10) || 0,
				currency: "RUB",
				expiresAt: parseInt(newMilestone.freespinsExpiresAt, 10) || 7,
			};
		} else if (newMilestone.rewardType.value === "chest") {
			const chestValidation = this.validateSelectedChest({
				chestPrizeId: newMilestone.chestPrizeId,
			});
			if (chestValidation) {
				this.props.Interface.addNoty("Error", chestValidation);
				return;
			}
			milestone.rewardConditions = {
				chestPrizeId: Number(newMilestone.chestPrizeId),
			};
		}

		this.setState({
			dailyProgressiveConfig: {
				...dailyProgressiveConfig,
				milestones: [...dailyProgressiveConfig.milestones, milestone],
			},
			newMilestone: { ...INITIAL_NEW_MILESTONE },
			availableMilestoneGames: [],
		});
	};

	removeMilestone = (id) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				milestones: prev.dailyProgressiveConfig.milestones.filter((m) => m.id !== id),
			},
		}));
	};

	updateNewMilestone = (patch) => {
		this.setState((prev) => ({
			newMilestone: {
				...prev.newMilestone,
				...patch,
			},
		}));
	};

	loadDailyConditionsSpinsGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const formattedGames = getGamesByProvider(gameState.data, provider);
		this.setState({ dailyConditionsSpinsGames: formattedGames });
	};

	getFilteredDailyConditionsSpinsGames = () => {
		return formatGamesForSelect(this.state.dailyConditionsSpinsGames || []);
	};

	getFilteredSpinsGames() {
		const games = this.props.achievementAddModal.availableSpinsGames || [];

		return games.map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));
	}

	getFilteredFreespinsGames() {
		const games = this.props.achievementAddModal.availableFreespinsGames || [];

		return games.map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));
	}

	getFilteredLevelGames() {
		const games = this.state.availableLevelGames || [];

		return games.map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));
	}

	getFilteredLevelSpinsGames() {
		const games = this.state.availableLevelSpinsGames || [];

		return games.map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));
	}

	handleImageChange = (event) => {
		const file = event.target.files[0];
		this.setState({ imageError: "" });

		if (file && (file.type === "image/svg+xml" || file.type === "image/png")) {
			if (this.props.achievementAddModal.image) {
				URL.revokeObjectURL(this.props.achievementAddModal.image);
			}
			const imageUrl = URL.createObjectURL(file);
			this.setState({ jsonImagePreview: "" });
			this.props.setAchievementAddModal({
				...this.props.achievementAddModal,
				image: imageUrl,
				imageFile: file,
			});
		} else {
			this.setState({ imageError: "Please select a valid SVG or PNG file" });
			this.props.setAchievementAddModal({
				...this.props.achievementAddModal,
				image: "",
				imageFile: null,
			});
		}
	};

	fileToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
			reader.readAsDataURL(file);
		});
	};

	getImageExtensionFromPath = (image) => {
		const extension = String(image || "")
			.split("?")[0]
			.split(".")
			.pop();
		return extension === "png" ? "png" : "svg";
	};

	getCleanLevels = (levels = []) => {
		return (levels || []).map(({ _rowId, ...level }) => level);
	};

	buildTransferData = () =>
		buildAchievementTransferData(
			{
				...this.props.achievementAddModal,
				levels: this.getCleanLevels(this.props.achievementAddModal.levels || []),
			},
			this.state.dailyProgressiveConfig,
		);

	getLevelDuplicateError = (levels = [], excludeRow = null) => {
		const seen = new Set();

		for (const item of levels || []) {
			if (excludeRow && this.isSameLevelRow(item, excludeRow)) continue;
			if (item?.level === undefined || item?.level === "") continue;

			const value = Number(item.level);
			if (!Number.isFinite(value)) continue;
			if (seen.has(value)) return "Level must be unique";
			seen.add(value);
		}

		return "";
	};

	isSameLevelRow = (left, right) => {
		if (!left || !right) return false;
		if (left._rowId && right._rowId) return left._rowId === right._rowId;
		return left === right;
	};

	getProviderOption = (providerId) => {
		return (this.props.config.game.options.provider || []).find((provider) => Number(provider.type) === Number(providerId)) || null;
	};

	getGameSelectOption = (games, gameId) => {
		const foundGame = (games || []).find((game) => Number(game.id || game.gameID) === Number(gameId));
		if (!foundGame) return null;
		return {
			label: foundGame.description || foundGame.name,
			value: foundGame.description || foundGame.name,
			gameId: foundGame.id || foundGame.gameID,
			game: foundGame,
		};
	};

	getLevelGamesByProvider = (providerId) => {
		const gameState = this.props.game;
		if (!providerId || !gameState?.loaded || !gameState?.data) return [];
		return gameState.data
			.filter((game) => Number(game.providerID) === Number(providerId) && game.active === 1)
			.map((game) => ({
				id: game.gameID,
				description: game.name,
				name: game.name,
				gameID: game.gameID,
				providerID: game.providerID,
			}));
	};

	getRewardTypeLabel = (rewardType) => {
		if (!rewardType) return "N/A";
		return rewardType.charAt(0).toUpperCase() + rewardType.slice(1);
	};

	getRewardText = (item) => {
		if (item.rewardType === "freespins") {
			const fs = item.freespins || {};
			return `${item.rewardValue ?? 0} FS, bet ${fs.betAmount ?? "-"}, providerId ${fs.providerId ?? ""}${fs.gameId ? `, gameId ${fs.gameId}` : ""}`;
		}
		if (item.rewardType === "chest") return this.getChestDisplayLabel(item.rewardConditions);
		if (item.rewardType === "money") return `${item.rewardValue ?? 0} money`;
		if (item.rewardType === "tickets" || item.tickets_reward) return `${item.rewardValue ?? item.tickets_reward ?? 0} tickets`;
		return "N/A";
	};

	getSpinsText = (item) => {
		return `providerId ${item.providerId ?? "N/A"}, gameId ${item.gameId ?? "N/A"}, amount ${item.amount ?? 0}`;
	};

	openLevelEditModal = (item) => {
		const freespinsProvider = item.freespins?.providerId ? { value: "endorphina", label: "Endorphina", type: Number(item.freespins.providerId) || 10 } : null;
		const freespinsGames = this.getLevelGamesByProvider(freespinsProvider?.type);
		const spinsProvider = this.getProviderOption(item.providerId);
		const spinsGames = this.getLevelGamesByProvider(spinsProvider?.type);

		this.setState({
			levelEditingRow: item,
			levelEditing: {
				level: item.level ?? "",
				progress_required: item.progress_required ?? "",
				rewardType: item.rewardType ? { value: item.rewardType, label: this.getRewardTypeLabel(item.rewardType) } : null,
				rewardValue: item.rewardValue ?? item.tickets_reward ?? "",
				amount: item.amount ?? "",
				triggerType: item.trigger_type ? { value: item.trigger_type, label: this.getRewardTypeLabel(item.trigger_type) } : null,
				freespinsProvider,
				freespinsGame: this.getGameSelectOption(freespinsGames, item.freespins?.gameId),
				freespinsBetAmount: item.freespins?.betAmount ?? "",
				freespinsExpiresAt: item.freespins?.expiresAt ?? "",
				spinsProvider,
				spinsGame: this.getGameSelectOption(spinsGames, item.gameId),
				spinsAmount: item.amount ?? "",
			},
			availableLevelGames: freespinsGames,
			availableLevelSpinsGames: spinsGames,
			levelError: "",
		});
	};

	updateLevelEditing = (patch) => {
		this.setState((prev) => ({
			levelEditing: {
				...(prev.levelEditing || {}),
				...patch,
			},
		}));
	};

	closeLevelEditModal = () => {
		this.setState({ levelEditingRow: null, levelEditing: null, levelError: "" });
	};

	validateLevelEdit = () => {
		const editing = this.state.levelEditing || {};
		const rewardType = editing.rewardType?.value;
		const typeValue = this.props.achievementAddModal.sourceType?.value;

		if (editing.level === undefined || editing.level === "") return "Level is required";
		if (editing.progress_required === undefined || editing.progress_required === "") return "Count is required";
		if (!rewardType) return "Reward type is required";
		if ((this.props.achievementAddModal.levels || []).some((item) => !this.isSameLevelRow(item, this.state.levelEditingRow) && Number(item.level) === Number(editing.level))) return "Level must be unique";

		if (typeValue === "deposit" || typeValue === "withdrawal") {
			if (editing.amount === undefined || editing.amount === "") return "Amount is required for deposit/withdrawal";
			if (!editing.triggerType) return "Trigger type is required for deposit/withdrawal";
		}

		if (rewardType === "freespins") {
			const spins = Number(editing.rewardValue || 0);
			if (!editing.freespinsProvider) return "Provider is required for freespins";
			if (!editing.freespinsGame) return "Game is required for freespins";
			if (!editing.freespinsBetAmount || Number(editing.freespinsBetAmount) <= 0) return "Bet amount must be > 0";
			if (!editing.freespinsExpiresAt) return "ExpiresAt is required for freespins";
			if (spins < 1 || spins > 1000) return "Reward Value (spins) must be 1-1000";
		} else if (editing.rewardValue === undefined || editing.rewardValue === "") {
			return "Reward value is required";
		}

		return "";
	};

	saveLevelEditModal = () => {
		const err = this.validateLevelEdit();
		if (err) {
			this.setState({ levelError: err });
			return;
		}

		const editing = this.state.levelEditing || {};
		const rewardType = editing.rewardType?.value;
		const typeValue = this.props.achievementAddModal.sourceType?.value;
		const nextLevels = (this.props.achievementAddModal.levels || []).map((item) => {
			if (!this.isSameLevelRow(item, this.state.levelEditingRow)) return item;

			const next = {
				...item,
				_rowId: item._rowId || crypto.randomUUID(),
				level: Number(editing.level),
				progress_required: Number(editing.progress_required) || 0,
				rewardType,
				rewardValue: Number(editing.rewardValue) || 0,
			};

			delete next.freespins;
			delete next.tickets_reward;

			if (rewardType === "tickets") {
				next.tickets_reward = Number(editing.rewardValue) || 0;
			}
			if (rewardType === "freespins") {
				next.freespins = {
					providerId: editing.freespinsProvider?.type || 10,
					gameId: editing.freespinsGame?.game?.id,
					betAmount: Number(editing.freespinsBetAmount) || 0,
					expiresAt: parseInt(editing.freespinsExpiresAt) || 0,
					currency: "RUB",
				};
			}
			if (typeValue === "deposit" || typeValue === "withdrawal") {
				next.amount = Number(editing.amount) || 0;
				next.trigger_type = editing.triggerType?.value || "both";
			}
			if (typeValue === "spins") {
				next.providerId = editing.spinsProvider?.type || null;
				next.gameId = editing.spinsGame?.game?.id || null;
				next.amount = Number(editing.spinsAmount || 0) || 0;
			}

			return next;
		});

		this.props.setAchievementAddModal({
			...this.props.achievementAddModal,
			levels: nextLevels,
		});
		this.closeLevelEditModal();
	};

	getCurrentJsonText = (validate = false) => {
		const data = this.buildTransferData();
		return validate ? getAchievementTransferJson(data) : stringifyAchievementTransfer(data);
	};

	getJsonTextareaValue = () => {
		if (this.state.jsonDirty) return this.state.jsonText;
		try {
			return this.getCurrentJsonText();
		} catch {
			return this.state.jsonText;
		}
	};

	copyAchievementJson = async () => {
		try {
			const jsonText = this.getCurrentJsonText(true);
			this.setState({ jsonText, jsonError: "", jsonDirty: false });
			await copyTextToClipboard(jsonText);
			this.props.Interface.addNoty("Success", "Achievement JSON copied");
		} catch (error) {
			this.setState({ jsonError: error.message || "Failed to copy JSON" });
			this.props.Interface.addNoty("Error", "Failed to copy achievement JSON");
		}
	};

	applyAchievementJson = () => {
		try {
			const data = parseAchievementTransferEnvelope(this.getJsonTextareaValue());
			const nextState = buildAddAchievementStateFromTransfer(this.props.achievementAddModal, data, this.props.config, this.props.game?.data || []);

			this.props.setAchievementAddModal(nextState);
			this.setState({
				jsonError: "",
				jsonDirty: false,
				jsonImagePreview: data.image || "",
				dailyProgressiveConfig: data.dailyProgressiveConfig || this.state.dailyProgressiveConfig,
				cohortGroups: buildCohortGroupsFromConditions(nextState.conditions),
			});

			if (data.rewardType === "chest" && data.rewardConditions?.chestPrizeId && !nextState.chestPrizeSelected) {
				this.loadChestPrizeOptions();
			}

			this.props.Interface.addNoty("Success", "Achievement JSON applied");
		} catch (error) {
			this.setState({ jsonError: error.message || "Invalid achievement JSON" });
			this.props.Interface.addNoty("Error", error.message || "Invalid achievement JSON");
		}
	};

	renderJsonImagePreview = () => {
		if (!this.state.jsonImagePreview) return null;

		return (
			<Alert type="info" header="JSON image preview">
				<SpaceBetween direction="vertical" size="s">
					<Box>Image from JSON is preview only. Upload the image manually for the current project CDN before saving.</Box>
					<Box textAlign="center">
						<img src={this.state.jsonImagePreview} alt="JSON achievement preview" style={{ maxWidth: "100%", maxHeight: "150px", padding: "4px" }} />
					</Box>
				</SpaceBetween>
			</Alert>
		);
	};

	validation() {
		try {
			const currentCategoryType = this.props.achievementAddModal.type?.value;

			if (currentCategoryType === "progressive") {
				const levels = this.props.achievementAddModal.levels || [];
				if (levels.length === 0) {
					return "Category type progressive required levels";
				}
				const duplicateLevelError = this.getLevelDuplicateError(levels);
				if (duplicateLevelError) return duplicateLevelError;
			} else if (currentCategoryType === "daily_progressive") {
				if (this.state.dailyProgressiveConfig.milestones.length === 0) {
					return "At least one milestone is required for daily progressive";
				}
			} else {
				if (this.props.achievementAddModal.rewardType?.value === "freespins") {
					if (!this.props.achievementAddModal.providerSelected) {
						return "Provider for freespins is required";
					}
					if (!this.props.achievementAddModal.gameSelected) {
						return "Game for freespins is required";
					}
					if (!this.props.achievementAddModal.betAmount) {
						return "Bet amount for freespins is required";
					}
					if (!this.props.achievementAddModal.expiresAt) {
						return "ExpiresAt for freespins is required";
					}

					const spinsCount = parseInt(this.props.achievementAddModal.rewardValue) || 0;
					if (!this.props.achievementAddModal.rewardValue || spinsCount < 1 || spinsCount > 1000) {
						return "Please enter rewardValue count (1-1000)";
					}

					const betAmount = parseFloat(this.props.achievementAddModal.betAmount) || 0;
					if (betAmount <= 0) {
						return "Please enter bet amount";
					}
				} else if (this.props.achievementAddModal.rewardType?.value === "chest") {
					const chestValidation = this.validateSelectedChest({
						chestPrizeId: this.props.achievementAddModal.chestPrizeId,
					});
					if (chestValidation) {
						return chestValidation;
					}
				} else if (!this.props.achievementAddModal.rewardType?.value) {
					return "Reward type is required";
				}
			}

			return "";
		} catch (e) {
			console.log("TVAR", e);
		}
	}

	validateJsonBeforeSave = () => {
		if (this.state.jsonImagePreview && !this.props.achievementAddModal.imageFile) {
			return "Upload image manually for the current project CDN";
		}
		if (!this.state.jsonDirty) return "";

		try {
			parseAchievementTransferEnvelope(this.getJsonTextareaValue());
			return "";
		} catch (error) {
			return error.message || "Invalid achievement JSON";
		}
	};

	addAchievement = async () => {
		try {
			this.props.setAchievementAddModal({
				...this.props.achievementAddModal,
				loaded: false,
			});

			let imageBase64 = "";
			let imageExtension = "";
			if (this.props.achievementAddModal.imageFile) {
				try {
					imageBase64 = await this.fileToBase64(this.props.achievementAddModal.imageFile);
					imageExtension = this.props.achievementAddModal.imageFile.type === "image/png" ? "png" : "svg";
				} catch (error) {
					console.error("Failed to convert image to base64:", error);
					this.props.Interface.addNoty("Error", "Failed to process image");
					this.props.setAchievementAddModal({
						...this.props.achievementAddModal,
						loaded: true,
					});
					return;
				}
			} else if (typeof this.props.achievementAddModal.image === "string" && this.props.achievementAddModal.image.length > 0) {
				imageBase64 = this.props.achievementAddModal.image;
				imageExtension = this.getImageExtensionFromPath(this.props.achievementAddModal.image);
			}

			const buildRewardConditions = () => {
				const rtype = this.props.achievementAddModal.rewardType?.value;
				if (rtype === "chest") {
					return {
						chestPrizeId: Number(this.props.achievementAddModal.chestPrizeId),
					};
				}

				if (rtype === "freespins") {
					return this.buildFreespinRewardConditions({
						gameSelected: this.props.achievementAddModal.gameSelected,
						betAmount: this.props.achievementAddModal.betAmount,
						expiresAt: this.props.achievementAddModal.expiresAt,
					});
				}

				return {};
			};

			const buildSpinsConditions = () => {
				const type = this.props.achievementAddModal.sourceType?.value;
				if (type === "spins") {
					if (this.props.achievementAddModal?.gameSpinsSelected?.gameId && this.props.achievementAddModal?.providerSpinsSelected?.type && this.props.achievementAddModal?.gameSpinsAmount) {
						return {
							gameId: this.props.achievementAddModal.gameSpinsSelected.gameId,
							providerId: this.props.achievementAddModal.providerSpinsSelected.type,
							amount: parseInt(this.props.achievementAddModal.gameSpinsAmount, 10),
						};
					} else if (this.props.achievementAddModal?.gameSpinsAmount) {
						return { amount: parseInt(this.props.achievementAddModal.gameSpinsAmount) };
					} else if (this.props.achievementAddModal?.gameSpinsSelected?.gameId && this.props.achievementAddModal?.providerSpinsSelected?.type) {
						return {
							gameId: this.props.achievementAddModal.gameSpinsSelected.gameId,
							providerId: this.props.achievementAddModal.providerSpinsSelected.type,
							amount: 0,
						};
					}
				}

				return {};
			};

			const validation = this.validation();

			if (validation?.length) {
				this.props.Interface.addNoty("Error", validation);
				this.props.setAchievementAddModal({
					...this.props.achievementAddModal,
					loaded: true,
				});
				return;
			}

			const jsonValidation = this.validateJsonBeforeSave();
			if (jsonValidation.length) {
				this.props.Interface.addNoty("Error", jsonValidation);
				this.props.setAchievementAddModal({
					...this.props.achievementAddModal,
					loaded: true,
				});
				return;
			}

			const buildDailyProgressiveConfig = () => {
				if (this.props.achievementAddModal.type?.value !== "daily_progressive") {
					return null;
				}
				const config = this.state.dailyProgressiveConfig;
				return {
					breakOnMissedDay: config.breakOnMissedDay,
					resetType: config.resetType,
					penaltyDays: config.penaltyDays,
					dailyConditions: config.dailyConditions,
					milestones: config.milestones.map((m) => ({
						day: m.day,
						rewardType: m.rewardType,
						rewardValue: m.rewardValue,
						rewardConditions: m.rewardConditions || {},
					})),
				};
			};
			const payload = {
				name: this.props.achievementAddModal.name,
				description: this.props.achievementAddModal.description,
				type: this.props.achievementAddModal.type?.value,
				sourceType: this.props.achievementAddModal.sourceType?.value || "",
				count: parseInt(this.props.achievementAddModal.count) || 0,
				rewardType: this.props.achievementAddModal.rewardType?.value || "",
				rewardValue: this.props.achievementAddModal.rewardType?.value === "chest" ? 0 : parseInt(this.props.achievementAddModal.rewardValue) || 0,
				image: imageBase64,
				image_extension: imageExtension.includes("png") ? "png" : "svg",
				link: this.props.achievementAddModal.link,
				active: parseInt(this.props.achievementAddModal.active.value),
				buttonText: this.props.achievementAddModal.buttonText,
				cycle: this.props.achievementAddModal.cycle?.value || null,
				cycleTimer: parseInt(this.props.achievementAddModal.cycleTimer) || 0,
				cycleUnit: this.props.achievementAddModal.cycleUnit?.value || null,
				order: parseInt(this.props.achievementAddModal.order),
				cohort: this.props.achievementAddModal.conditions || [],
				nameTranslationType: this.props.achievementAddModal.nameTranslationType,
				buttonTranslationType: this.props.achievementAddModal.buttonTranslationType,
				descriptionTranslationType: this.props.achievementAddModal.descriptionTranslationType,
				levels: this.getCleanLevels(this.props.achievementAddModal.levels || []),
				startDate: this.props.achievementAddModal.startDate,
				endDate: this.props.achievementAddModal.endDate,
				amount: parseInt(this.props.achievementAddModal?.amount, 10) || 0,
				triggerType: this.props.achievementAddModal?.triggerType?.value || "",
				rewardConditions: buildRewardConditions(),
				spinsConditions: buildSpinsConditions(),
				...(this.props.achievementAddModal.type?.value === "daily_progressive" ? { dailyProgressiveConfig: buildDailyProgressiveConfig() } : {}),
			};

			this.props.Interface.request("set", { class: "data", module: "achievement", method: "create" }, payload)
				.then(() => {
					this.props.Interface.addNoty("Success", `Achievement "${this.props.achievementAddModal.name}" was successfully added`);

					if (this.props.achievementAddModal.image) {
						URL.revokeObjectURL(this.props.achievementAddModal.image);
					}

					this.props.setAchievementAddModal({
						show: false,
						loaded: true,
						name: "",
						description: "",
						type: "",
						rewardType: "",
						rewardValue: "",
						image: "",
						imageFile: null,
						link: "",
						count: "",
						sourceType: null,
						active: false,
						buttonText: "",
						cycle: null,
						cycleUnit: null,
						cycleTimer: null,
						order: "",
						categories: this.props.achievementAddModal.categories,
						conditions: [],
						gameSpinsAmount: 0,
						levels: [],
						providerSpinsSelected: null,
						gameSpinsSelected: null,
						availableSpinsGames: [],
						providerSelected: null,
						gameSelected: null,
						availableFreespinsGames: [],
						chestRewards: [],
						chestType: "",
						chestPrizeId: null,
						chestPrizeSelected: null,
						chestPrizeOptions: [],
						chestPrizeLoading: false,
						chestPrizeMatchMissing: false,
					});
					this.setState({
						imageError: "",
						levelError: "",
						availableLevelGames: [],
						availableLevelSpinsGames: [],
						newSpinsProvider: null,
						newSpinsGame: null,
						newChestRewards: [],
						newChestType: "",
						newChestPrizeId: null,
						newChestPrizeSelected: null,
						newSpinsAmount: "",
						levelEditingRow: null,
						levelEditing: null,
						dailyProgressiveConfig: {
							breakOnMissedDay: false,
							resetType: "full",
							penaltyDays: 0,
							dailyConditions: {
								type: "deposit",
								deposit: { amount: 0, paymentType: "both" },
								spins: { count: 0, minBet: 0, gameId: null, providerId: null },
							},
							milestones: [],
						},
						newMilestone: {
							day: "",
							rewardType: null,
							rewardValue: "",
							freespinsProvider: null,
							freespinsGame: null,
							freespinsBetAmount: "",
							freespinsExpiresAt: "",
							chestRewards: [],
							chestType: "",
							chestPrizeId: null,
							chestPrizeSelected: null,
						},
						availableMilestoneGames: [],
						dailyConditionsSpinsGames: [],
						jsonText: "",
						jsonError: "",
						jsonDirty: false,
						jsonImagePreview: "",
					});

					this.props.Interface.loadAchievementList();
				})
				.catch((error) => {
					console.error("Failed to add achievement:", error);
					this.props.setAchievementAddModal({
						...this.props.achievementAddModal,
						loaded: true,
					});
					this.props.Interface.addNoty("Error", "Failed to create achievement");
				});
		} catch (e) {
			console.log(e);
			this.props.Interface.addNoty("Error", e.text);
			this.props.setAchievementAddModal({
				...this.props.achievementAddModal,
				loaded: true,
			});
		}
	};

	validateNewLevel = () => {
		const t = this.state?.newRewardType?.value;

		if (!this.state?.newLevel) return "Level is required";
		if (!this.state?.newProgress && this.state?.newProgress !== 0) return "Count is required";
		if (!t) return "Reward type is required";
		if ((this.props.achievementAddModal.levels || []).some((item) => Number(item.level) === Number(this.state.newLevel))) {
			return "Level must be unique";
		}

		if (this.props.achievementAddModal.sourceType?.value === "deposit" || this.props.achievementAddModal.sourceType?.value === "withdrawal") {
			if (this.state?.newAmount === undefined || this.state?.newAmount === "") return "Amount is required for deposit/withdrawal";
			if (!this.state?.newTriggerType) return "Trigger type is required for deposit/withdrawal";
		}

		if (t === "freespins") {
			const spins = Number(this.state?.newRewardValue || 0);
			if (!this.state?.newProvider) return "Provider is required for freespins";
			if (!this.state?.newGame) return "Game is required for freespins";
			if (!this.state?.newBetAmount || Number(this.state?.newBetAmount) <= 0) return "Bet amount must be > 0";
			if (!this.state?.newExpiresAt) return "ExpiresAt is required for freespins";
			if (spins < 1 || spins > 1000) return "Reward Value (spins) must be 1-1000";
		} else if (t === "chest") {
			return this.validateSelectedChest({
				chestPrizeId: this.state?.newChestPrizeId,
			});
		} else if (t === "spins") {
			if (this.state?.newRewardValue === undefined || this.state?.newRewardValue === "") return "Reward value is required";
		} else {
			if (this.state?.newRewardValue === undefined || this.state?.newRewardValue === "") {
				return "Reward value is required";
			}
		}

		return "";
	};

	renderNewLevelChestConfig = () => {
		return (
			<Container header={<Box variant="h4">Chest preset</Box>}>
				<SpaceBetween direction="vertical" size="m">
					<FormField label="Chest" stretch>
						<Select
							filteringType="auto"
							placeholder="Select chest preset"
							selectedOption={this.state.newChestPrizeSelected}
							onChange={({ detail }) => this.selectNewLevelChestPrize(detail.selectedOption)}
							options={this.props.achievementAddModal.chestPrizeOptions}
							statusType={this.props.achievementAddModal.chestPrizeLoading ? "loading" : "finished"}
							loadingText="Loading chest presets"
							empty="No chest presets found"
						/>
					</FormField>

					{this.state.newChestType && (
						<Box>{`Chest type: ${formatChestPrizeType(this.state.newChestType)}`}</Box>
					)}

					{(this.state.newChestRewards || []).length > 0 && (
						<Box>{`Rewards in chest: ${(this.state.newChestRewards || []).length}`}</Box>
					)}
				</SpaceBetween>
			</Container>
		);
	};

	render() {
		const typeValue = this.props.achievementAddModal.sourceType?.value;

		return (
			<Modal
				visible={this.props.achievementAddModal.show}
				onDismiss={() => {
					if (this.props.achievementAddModal.image) {
						URL.revokeObjectURL(this.props.achievementAddModal.image);
					}
					this.onDismiss();
					this.setState({
						imageError: "",
						levelError: "",
						availableLevelGames: [],
						availableLevelSpinsGames: [],
						newSpinsProvider: null,
						newSpinsGame: null,
						newChestRewards: [],
						newChestType: "",
						newChestPrizeId: null,
						newChestPrizeSelected: null,
						newSpinsAmount: "",
						levelEditingRow: null,
						levelEditing: null,
						dailyProgressiveConfig: {
							breakOnMissedDay: false,
							resetType: "full",
							penaltyDays: 0,
							dailyConditions: {
								type: "deposit",
								deposit: { amount: 0, paymentType: "both" },
								spins: { count: 0, minBet: 0, gameId: null, providerId: null },
							},
							milestones: [],
						},
						newMilestone: {
							day: "",
							rewardType: null,
							rewardValue: "",
							freespinsProvider: null,
							freespinsGame: null,
							freespinsBetAmount: "",
							freespinsExpiresAt: "",
							chestRewards: [],
							chestType: "",
							chestPrizeId: null,
							chestPrizeSelected: null,
						},
						availableMilestoneGames: [],
						dailyConditionsSpinsGames: [],
						jsonText: "",
						jsonError: "",
						jsonDirty: false,
						jsonImagePreview: "",
					});
				}}
				header="Add Achievement"
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="link"
								onClick={() => {
									this.onDismiss();
								}}>
								Cancel
							</Button>
							<Button
								variant="primary"
								iconName="check"
								onClick={this.addAchievement}
								disabled={
									!this.props.achievementAddModal.name ||
									!this.props.achievementAddModal.description ||
									!this.props.achievementAddModal.type ||
									(this.props.achievementAddModal?.cycle?.value === "replay" ? !this.props.achievementAddModal.cycleUnit || !this.props.achievementAddModal.cycleTimer : !this.props.achievementAddModal.startDate || !this.props.achievementAddModal.endDate) ||
									!this.props.achievementAddModal.buttonText ||
									!this.props.achievementAddModal.active ||
									!this.props.achievementAddModal.order ||
									!this.props.achievementAddModal.link ||
									!this.props.achievementAddModal.image ||
									!this.props.achievementAddModal.nameTranslationType ||
									!this.props.achievementAddModal.buttonTranslationType ||
									!this.props.achievementAddModal.descriptionTranslationType ||
									(this.props.achievementAddModal?.type?.value !== "progressive" && this.props.achievementAddModal?.type?.value !== "daily_progressive"
										? !this.props.achievementAddModal.count ||
											(this.props.achievementAddModal.rewardType?.value !== "chest" && !this.props.achievementAddModal.rewardValue)
										: false)
								}
								loading={!this.props.achievementAddModal.loaded}>
								Create Achievement
							</Button>
						</SpaceBetween>
					</Box>
				}>
				{this.props.achievementAddModal.loaded ? (
					<Form>
						{this.state.imageError && (
							<Alert type="error" dismissible onDismiss={() => this.setState({ imageError: "" })}>
								{this.state.imageError}
							</Alert>
						)}

						<Tabs
							tabs={[
								// === General ===
								{
									label: "General",
									id: "general",
									content: (
										<SpaceBetween direction="vertical" size="l">
											<Container header={<Box variant="h3">General</Box>}>
												<SpaceBetween direction="vertical" size="m">
													<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
														<FormField description="Translation achievement name" label="Achievement Name" stretch>
															<Input
																placeholder="Enter achievement name"
																value={this.props.achievementAddModal.name}
																onChange={(e) =>
																	this.props.setAchievementAddModal({
																		...this.props.achievementAddModal,
																		name: e.detail.value,
																	})
																}
															/>
														</FormField>

														<FormField description="Name translation type" label="Name translation type" stretch>
															<Select
																selectedOption={this.props.achievementAddModal.nameTranslationType ? { value: this.props.achievementAddModal.nameTranslationType, label: this.props.achievementAddModal.nameTranslationType.charAt(0).toUpperCase() + this.props.achievementAddModal.nameTranslationType.slice(1) } : null}
																onChange={(e) => {
																	this.props.setAchievementAddModal({
																		...this.props.achievementAddModal,
																		nameTranslationType: e.detail.selectedOption.value,
																	});
																}}
																options={[
																	{ value: "local", label: "Local" },
																	{ value: "international", label: "International" },
																	{ value: "mixed", label: "Mixed" },
																]}
																placeholder="Select achievement name translation type"
															/>
														</FormField>

														<FormField description="Reward description" label="Reward description" stretch>
															<Input
																placeholder="Enter reward description"
																value={this.props.achievementAddModal.description}
																onChange={(e) =>
																	this.props.setAchievementAddModal({
																		...this.props.achievementAddModal,
																		description: e.detail.value,
																	})
																}
															/>
														</FormField>

														<FormField description="Reward description translation type" label="Reward description translation type" stretch>
															<Select
																selectedOption={this.props.achievementAddModal.descriptionTranslationType ? { value: this.props.achievementAddModal.descriptionTranslationType, label: this.props.achievementAddModal.descriptionTranslationType.charAt(0).toUpperCase() + this.props.achievementAddModal.descriptionTranslationType.slice(1) } : null}
																onChange={(e) => {
																	this.props.setAchievementAddModal({
																		...this.props.achievementAddModal,
																		descriptionTranslationType: e.detail.selectedOption.value,
																	});
																}}
																options={[
																	{ value: "local", label: "Local" },
																	{ value: "international", label: "International" },
																	{ value: "mixed", label: "Mixed" },
																]}
																placeholder="Select reward description translation type"
															/>
														</FormField>
														<FormField description="Achievement type" label="Achievement type" stretch>
															<Select
																selectedOption={this.props.achievementAddModal.type}
																onChange={({ detail }) =>
																	this.props.setAchievementAddModal({
																		...this.props.achievementAddModal,
																		type: detail.selectedOption,
																	})
																}
																options={[
																	{ value: "daily", label: "daily" },
																	{ value: "unique", label: "unique" },
																	{ value: "progressive", label: "progressive" },
																	{ value: "daily_progressive", label: "daily progressive" },
																]}
																placeholder="Select a type"
															/>
														</FormField>

														{this.props.achievementAddModal?.type?.value !== "daily_progressive" && (
															<FormField description="Achievement source type" label="Achievement source type" stretch>
																<Select
																	selectedOption={this.props.achievementAddModal.sourceType}
																	onChange={({ detail }) => {
																		const newType = detail.selectedOption.value;
																		const updates = { sourceType: detail.selectedOption };
																		if (newType !== "spins") {
																			updates.providerSpinsSelected = null;
																			updates.gameSpinsSelected = null;
																			updates.availableSpinsGames = [];
																		}
																		if (newType === "verify" || newType === "email_verified" || newType === "phone_verified" || newType === "tg_verified") {
																			this.props.setAchievementAddModal({
																				...this.props.achievementAddModal,
																				...updates,
																				count: 1,
																				category_id: null,
																				levels: [],
																			});
																		} else {
																			this.props.setAchievementAddModal({
																				...this.props.achievementAddModal,
																				...updates,
																				count: 0,
																			});
																		}
																	}}
																	options={[
																		{ value: "deposit", label: "Deposit" },
																		{ value: "withdrawal", label: "Withdrawal" },
																		{ value: "verify", label: "Verify" },
																		{ value: "email_verified", label: "Email verified" },
																		{ value: "phone_verified", label: "Phone verified" },
																		{ value: "tg_verified", label: "Telegram verified" },
																		{ value: "vip_lvl", label: "Vip lvl" },
																		{ value: "spins", label: "Spins" },
																		{ value: "mobile", label: "Mobile" },
																		{ value: "referral", label: "Referral" },
																		{ value: "tasks", label: "Tasks" },
																	]}
																	placeholder="Select a source type"
																/>
															</FormField>
														)}
													</Grid>

													{this.props.achievementAddModal?.type?.value !== "progressive" && this.props.achievementAddModal?.type?.value !== "daily_progressive" && (
														<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
															<FormField description="Reward type" label="Reward type" stretch>
																<Select
																	selectedOption={this.props.achievementAddModal.rewardType}
																	onChange={({ detail }) => {
																		this.props.setAchievementAddModal({
																			...this.props.achievementAddModal,
																			rewardType: detail.selectedOption,
																			gameSelected: null,
																			availableFreespinsGames: [],
																			providerSelected: null,
																			rewardValue: "",
																			betAmount: "",
																			expiresAt: "",
																			chestRewards: [],
																			chestType: "",
																			chestPrizeId: null,
																			chestPrizeSelected: null,
																			chestPrizeOptions: detail.selectedOption.value === "chest" ? this.props.achievementAddModal.chestPrizeOptions : [],
																			chestPrizeLoading: false,
																			chestPrizeMatchMissing: false,
																		});

																		if (detail.selectedOption.value === "chest") {
																			this.loadChestPrizeOptions();
																		}
																	}}
																	options={[
																		{ value: "money", label: "Money" },
																		{ value: "freespins", label: "Freespins" },
																		{ value: "tickets", label: "Tickets" },
																		{ value: "chest", label: "Chest" },
																	]}
																	placeholder="Select a reward type"
																/>
															</FormField>
															{this.props.achievementAddModal.rewardType?.value !== "chest" && (
																<FormField description="Reward value" label="Reward value" stretch>
																	<Input
																		type="number"
																		placeholder="Enter reward value"
																		value={this.props.achievementAddModal.rewardValue}
																		onChange={(e) =>
																			this.props.setAchievementAddModal({
																				...this.props.achievementAddModal,
																				rewardValue: e.detail.value,
																			})
																		}
																	/>
																</FormField>
															)}
														</Grid>
													)}

													<FormField description="Achievement order [-...inf] " label="Achievement order" stretch>
														<Input
															type="number"
															placeholder="Enter order"
															value={this.props.achievementAddModal.order}
															onChange={(e) =>
																this.props.setAchievementAddModal({
																	...this.props.achievementAddModal,
																	order: e.detail.value,
																})
															}
														/>
													</FormField>
												</SpaceBetween>
											</Container>

											{typeValue === "spins" && this.props.achievementAddModal?.type?.value !== "progressive" && this.props.achievementAddModal?.type?.value !== "daily_progressive" && (
												<Container header={<Box variant="h3">Game params</Box>}>
													<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
														<FormField description="Selected provider" stretch label="Provider">
															{/* TODO: The sad */}
															<Select
																filteringType="auto"
																placeholder="Select Provider"
																selectedOption={this.props.achievementAddModal.providerSpinsSelected}
																onChange={(option) => {
																	this.props.setAchievementAddModal({
																		...this.props.achievementAddModal,
																		providerSpinsSelected: option.detail.selectedOption,
																		gameSpinsSelected: null,
																		availableSpinsGames: [],
																	});
																	this.loadAvailableSpinsGames(option.detail.selectedOption.type);
																}}
																options={this.props.config.game.options.provider}
															/>
														</FormField>

														{this.props.achievementAddModal.providerSpinsSelected && (
															<FormField description="Selected game" stretch label="Game">
																<Select
																	filteringType="auto"
																	placeholder="Select Game (type to search...)"
																	selectedOption={this.props.achievementAddModal.gameSpinsSelected}
																	onChange={(option) => {
																		this.props.setAchievementAddModal({
																			...this.props.achievementAddModal,
																			gameSpinsSelected: option.detail.selectedOption,
																		});
																	}}
																	options={this.getFilteredSpinsGames()}
																/>
															</FormField>
														)}

														<FormField description="Spins amount only coops" label="Spins amount more than" stretch>
															<Input
																placeholder="Spins amount (coops)"
																type="number"
																value={this.props.achievementAddModal.gameSpinsAmount}
																onChange={(e) =>
																	this.props.setAchievementAddModal({
																		...this.props.achievementAddModal,
																		gameSpinsAmount: e.detail.value,
																	})
																}
															/>
														</FormField>
													</Grid>
												</Container>
											)}

											{this.props.achievementAddModal.rewardType?.value === "freespins" && this.props.achievementAddModal?.type?.value !== "progressive" && this.props.achievementAddModal?.type?.value !== "daily_progressive" && (
												<Container header={<Box variant="h3">Freespins params</Box>}>
													<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
														<FormField description="Selected provider" stretch label="Provider">
															<Select
																filteringType="auto"
																placeholder="Select Provider"
																selectedOption={this.props.achievementAddModal.providerSelected}
																onChange={(option) => {
																	this.props.setAchievementAddModal({
																		...this.props.achievementAddModal,
																		providerSelected: option.detail.selectedOption,
																		gameSelected: null,
																		availableFreespinsGames: [],
																	});
																	this.loadAvailableFreespinsGames(option.detail.selectedOption.type);
																}}
																options={[{ label: "Endorphina", value: "endorphina", type: 10 }]}
															/>
														</FormField>

														{this.props.achievementAddModal.providerSelected && (
															<>
																<FormField description="Selected game" stretch label="Game">
																	<Select
																		filteringType="auto"
																		placeholder="Select Game (type to search...)"
																		selectedOption={this.props.achievementAddModal.gameSelected}
																		onChange={(option) => {
																			this.props.setAchievementAddModal({
																				...this.props.achievementAddModal,
																				gameSelected: option.detail.selectedOption,
																			});
																		}}
																		options={this.getFilteredFreespinsGames()}
																	/>
																</FormField>

																<FormField description="Bet amount (e.g., 100 for 100.00 RUB)" stretch label="Bet Amount">
																	<Input
																		type="number"
																		inputMode="decimal"
																		placeholder="Enter bet amount (e.g., 100 for 100.00 RUB)"
																		value={this.props.achievementAddModal.betAmount || ""}
																		onChange={(input) => {
																			this.props.setAchievementAddModal({
																				...this.props.achievementAddModal,
																				betAmount: input.detail.value,
																			});
																		}}
																	/>
																</FormField>

																<FormField description="Expired fs date (Number) (1 - day .. eg)" stretch label="Expiration Date">
																	<Input
																		placeholder="Enter expiration date"
																		value={this.props.achievementAddModal.expiresAt || ""}
																		onChange={(input) => {
																			this.props.setAchievementAddModal({
																				...this.props.achievementAddModal,
																				expiresAt: input.detail.value,
																			});
																		}}
																	/>
																</FormField>
															</>
														)}
													</Grid>
												</Container>
											)}

											{this.props.achievementAddModal.rewardType?.value === "chest" && this.props.achievementAddModal?.type?.value !== "progressive" && this.props.achievementAddModal?.type?.value !== "daily_progressive" && (
												<Container header={<Box variant="h3">Chest preset</Box>}>
													<SpaceBetween direction="vertical" size="m">
														<FormField label="Chest" stretch>
															<Select
																filteringType="auto"
																placeholder="Select chest preset"
																selectedOption={this.props.achievementAddModal.chestPrizeSelected}
																onChange={({ detail }) => this.selectChestPrize(detail.selectedOption)}
																options={this.props.achievementAddModal.chestPrizeOptions}
																statusType={this.props.achievementAddModal.chestPrizeLoading ? "loading" : "finished"}
																loadingText="Loading chest presets"
																empty="No chest presets found"
															/>
														</FormField>

														{this.props.achievementAddModal.chestType && (
															<Box>{`Chest type: ${formatChestPrizeType(this.props.achievementAddModal.chestType)}`}</Box>
														)}

														{(this.props.achievementAddModal.chestRewards || []).length > 0 && (
															<Box>{`Rewards in chest: ${(this.props.achievementAddModal.chestRewards || []).length}`}</Box>
														)}
													</SpaceBetween>
												</Container>
											)}

											<Container header={<Box variant="h3">Parameters</Box>}>
												<Grid gridDefinition={[{ colspan: 12 }]}>
													{this.props.achievementAddModal?.type?.value !== "progressive" && this.props.achievementAddModal?.type?.value !== "daily_progressive" && (
														<>
															<FormField description={this.props.achievementAddModal.sourceType?.value ? `Achievement count of ${this.props.achievementAddModal.sourceType?.value}` : "Achievement count"} label="Count" stretch>
																<Input
																	type="number"
																	placeholder={this.props.achievementAddModal.sourceType?.value ? `Enter count of ${this.props.achievementAddModal.sourceType?.value}` : "Enter count"}
																	value={this.props.achievementAddModal.count}
																	onChange={(e) =>
																		this.props.setAchievementAddModal({
																			...this.props.achievementAddModal,
																			count: e.detail.value,
																		})
																	}
																	disabled={this.props.achievementAddModal.sourceType?.value === "verify" || this.props.achievementAddModal.sourceType?.value === "email_verified" || this.props.achievementAddModal.sourceType?.value === "phone_verified" || this.props.achievementAddModal.sourceType?.value === "tg_verified"}
																/>
															</FormField>
														</>
													)}
												</Grid>

												{this.props.achievementAddModal?.type?.value !== "progressive" && this.props.achievementAddModal?.type?.value !== "daily_progressive" && (this.props.achievementAddModal.sourceType?.value === "deposit" || this.props.achievementAddModal.sourceType?.value === "withdrawal") && (
													<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
														<>
															<FormField description="Deposit amount" label="Amount" stretch>
																<Input
																	type="number"
																	placeholder="Enter amount"
																	value={this.props.achievementAddModal.amount}
																	onChange={(e) =>
																		this.props.setAchievementAddModal({
																			...this.props.achievementAddModal,
																			amount: e.detail.value,
																		})
																	}
																/>
															</FormField>

															<FormField description="Deposit type" label="Type" stretch>
																<Select
																	selectedOption={this.props.achievementAddModal.triggerType}
																	onChange={({ detail }) =>
																		this.props.setAchievementAddModal({
																			...this.props.achievementAddModal,
																			triggerType: detail.selectedOption,
																		})
																	}
																	options={[
																		{ value: "crypto", label: "Crypto" },
																		{ value: "fiat", label: "Fiat" },
																		{ value: "both", label: "Both" },
																	]}
																	placeholder="Select type"
																/>
															</FormField>
														</>
													</Grid>
												)}

												<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
													<FormField description="Button translation text" label="Button Text" stretch>
														<Input
															placeholder="Enter button text"
															value={this.props.achievementAddModal.buttonText}
															onChange={(e) =>
																this.props.setAchievementAddModal({
																	...this.props.achievementAddModal,
																	buttonText: e.detail.value,
																})
															}
														/>
													</FormField>
													<FormField description="Button translation type" label="Button translation type" stretch>
														<Select
															selectedOption={this.props.achievementAddModal.buttonTranslationType ? { value: this.props.achievementAddModal.buttonTranslationType, label: this.props.achievementAddModal.buttonTranslationType.charAt(0).toUpperCase() + this.props.achievementAddModal.buttonTranslationType.slice(1) } : null}
															onChange={(e) => {
																this.props.setAchievementAddModal({
																	...this.props.achievementAddModal,
																	buttonTranslationType: e.detail.selectedOption.value,
																});
															}}
															options={[
																{ value: "local", label: "Local" },
																{ value: "international", label: "International" },
																{ value: "mixed", label: "Mixed" },
															]}
															placeholder="Select button translation type"
														/>
													</FormField>
												</Grid>
											</Container>

											<Container header={<Box variant="h3">Cycle timer</Box>}>
												<Grid gridDefinition={this.props.achievementAddModal?.cycle?.value === "replay" ? [{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }] : [{ colspan: 12 }]}>
													<FormField description="Cycle of achievement" label="Cycle" stretch>
														<Select
															selectedOption={this.props.achievementAddModal.cycle}
															onChange={({ detail }) =>
																this.props.setAchievementAddModal({
																	...this.props.achievementAddModal,
																	cycle: detail.selectedOption,
																})
															}
															options={[
																{ value: "replay", label: "Replay" },
																{ value: "not", label: "Not repeatable" },
															]}
															placeholder="Select cycle"
														/>
													</FormField>

													{this.props.achievementAddModal?.cycle?.value === "replay" && (
														<>
															<FormField description="Cycle unit hours of day" label="Cycle Unit" stretch>
																<Select
																	selectedOption={this.props.achievementAddModal.cycleUnit}
																	onChange={({ detail }) =>
																		this.props.setAchievementAddModal({
																			...this.props.achievementAddModal,
																			cycleUnit: detail.selectedOption,
																		})
																	}
																	options={[
																		{ value: "hour", label: "Hour" },
																		{ value: "day", label: "Day" },
																	]}
																	placeholder="Select unit"
																/>
															</FormField>

															<FormField description="Number of hours of day" label="Cycle Timer" stretch>
																<Input
																	type="number"
																	placeholder="Enter timer"
																	value={this.props.achievementAddModal.cycleTimer}
																	onChange={(e) =>
																		this.props.setAchievementAddModal({
																			...this.props.achievementAddModal,
																			cycleTimer: e.detail.value,
																		})
																	}
																/>
															</FormField>
														</>
													)}
												</Grid>
												<Box margin={{ top: "m" }}>
													<FormField description="Date from/to" label="Expired achievement date" stretch>
														<DateRangePicker
															onChange={({ detail }) => {
																this.props.setAchievementAddModal({
																	...this.props.achievementAddModal,
																	startDate: detail.value.startDate.split("+")[0],
																	endDate: detail.value.endDate.split("+")[0],
																});
															}}
															value={{
																startDate: this.props.achievementAddModal.startDate,
																endDate: this.props.achievementAddModal.endDate,
																type: "absolute",
															}}
															relativeOptions={[]}
															isValidRange={(range) => {
																if (range.type === "absolute") {
																	const [startDateWithoutTime] = range.startDate.split("T");
																	const [endDateWithoutTime] = range.endDate.split("T");
																	if (!startDateWithoutTime || !endDateWithoutTime) {
																		return {
																			valid: false,
																			errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
																		};
																	}
																	if (new Date(range.startDate) - new Date(range.endDate) > 0) {
																		return {
																			valid: false,
																			errorMessage: "The selected date range is invalid. The start date must be before the end date.",
																		};
																	}
																} else {
																	return { valid: false };
																}
															}}
															i18nStrings={{
																todayAriaLabel: "Today",
																nextMonthAriaLabel: "Next month",
																previousMonthAriaLabel: "Previous month",
																customRelativeRangeDurationLabel: "Duration",
																customRelativeRangeDurationPlaceholder: "Enter duration",
																customRelativeRangeOptionLabel: "Custom range",
																customRelativeRangeOptionDescription: "Define a custom range in the past",
																customRelativeRangeUnitLabel: "Unit of time",
																formatRelativeRange: ({ amount, unit, startDate, endDate }) => {
																	const start = new Date(startDate);
																	const end = new Date(endDate);

																	const locale = "en-GB";
																	const dateOptions = { year: "numeric", month: "long", day: "numeric" };
																	const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

																	const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
																	const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

																	return `${formattedStart} - ${formattedEnd}`;
																},
																formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
																dateTimeConstraintText: "Date and time must be within the selected range.",
																renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
																labelRelativeRange: "Relative range",
																labelAbsoluteRange: "Absolute range",
																dateTimePickerAriaLabel: "Date and time picker",
																dateTimePickerLabel: "Date and Time",
																clearButtonAriaLabel: "Clear date range",
																relativeModeTitle: "Relative mode",
																absoluteModeTitle: "Absolute mode",
																cancelButtonLabel: "Cancel",
																applyButtonLabel: "Apply",
															}}
															placeholder="Expired date from/to"
														/>
													</FormField>
												</Box>
											</Container>

											<Container header={<Box variant="h3">Other</Box>}>
												<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
													<FormField description="Link button text" label="Link" stretch>
														<Input
															type="url"
															placeholder="Enter URL"
															value={this.props.achievementAddModal.link}
															onChange={(e) =>
																this.props.setAchievementAddModal({
																	...this.props.achievementAddModal,
																	link: e.detail.value,
																})
															}
														/>
													</FormField>

													<FormField description="Achievement active" label="Active" stretch>
														<Select
															selectedOption={this.props.achievementAddModal.active}
															onChange={({ detail }) =>
																this.props.setAchievementAddModal({
																	...this.props.achievementAddModal,
																	active: detail.selectedOption,
																})
															}
															options={[
																{ value: "1", label: "Enabled" },
																{ value: "0", label: "Disabled" },
															]}
															placeholder="Select active"
														/>
													</FormField>
												</Grid>
											</Container>
										</SpaceBetween>
									),
								},
								{
									label: "Cohort",
									id: "cohort",
									content: (
										<SpaceBetween size="l">
											<Box>
												<Button variant="primary" iconName="add-plus" onClick={this.addCohortGroup}>
													Add group
												</Button>
											</Box>

											{(this.state.cohortGroups || []).map((group, groupIndex) => {
												const moduleOptions = this.getCohortModuleOptions();
												const logicalOperatorOptions = this.getLogicalOperatorOptions();

												const selectedModule = moduleOptions.find((o) => o.value === group.module) || null;

												const groupOperatorValue = groupIndex === 0 ? "AND" : group.groupLogicalOperator || "AND";
												const selectedGroupLogicalOperator = logicalOperatorOptions.find((o) => o.value === groupOperatorValue) || null;

												const groupConditions = (this.props.achievementAddModal.conditions || []).filter((c) => c.groupId === group.groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

												const newCondition = this.state.newConditionByGroupId?.[group.groupId] || {};
												const selectedConditionLogicalOperator = logicalOperatorOptions.find((o) => o.value === (newCondition.logicalOperator || "AND")) || null;

												const fieldOptions = this.getCohortFieldOptionsByModule(group.module);

												const selectedFieldOption = newCondition.field ? { value: newCondition.field, label: newCondition.fieldLabel } : null;

												const selectedOperatorOption = newCondition.operator ? { value: newCondition.operator, label: newCondition.operator } : null;

												const isAddDisabled = !newCondition.field || this.isConditionValueEmpty(newCondition.value) || (!newCondition.operator && newCondition.conditionalType !== "select" && newCondition.conditionalType !== "multiselect");

												return (
													<Container
														key={group.groupId}
														header={
															<Header
																variant="h3"
																actions={
																	<Button variant="icon" iconName="delete-marker" onClick={() => this.removeCohortGroup(group.groupId)}>
																		Remove group
																	</Button>
																}>
																{`Group ${groupIndex + 1}`}
															</Header>
														}>
														<SpaceBetween size="m">
															<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
																<FormField label="Module" stretch>
																	<Select selectedOption={selectedModule} onChange={({ detail }) => this.changeCohortGroupModule(group.groupId, detail.selectedOption.value)} options={moduleOptions} placeholder="Select module" />
																</FormField>

																<FormField label="Group operator" stretch>
																	<Select
																		selectedOption={selectedGroupLogicalOperator}
																		onChange={({ detail }) => {
																			if (groupIndex === 0) return;
																			this.updateCohortGroup(group.groupId, {
																				groupLogicalOperator: detail.selectedOption.value,
																			});
																		}}
																		options={logicalOperatorOptions}
																		placeholder="Select operator"
																		disabled={groupIndex === 0}
																	/>
																</FormField>

																<Box />
															</Grid>

															<Table
																columnDefinitions={[
																	{ id: "logic", header: "Logic", cell: (item) => item.logicalOperator || "-" },
																	{ id: "field", header: "Field", cell: (item) => item.fieldLabel || item.field },
																	{ id: "operator", header: "Operator", cell: (item) => item.operator },
																	{ id: "value", header: "Value", cell: (item) => (Array.isArray(item.value) ? item.value.join(", ") : item.value) },
																	{
																		id: "actions",
																		header: "Control",
																		cell: (item) => (
																			<Button
																				variant="icon"
																				iconName="delete-marker"
																				onClick={() => {
																					const next = this.normalizeGroupConditions(
																						(this.props.achievementAddModal.conditions || []).filter((c) => c.id !== item.id),
																						group.groupId,
																					);
																					this.props.setAchievementAddModal({
																						...this.props.achievementAddModal,
																						conditions: next,
																					});
																				}}>
																				Remove
																			</Button>
																		),
																	},
																]}
																footer={
																	<FormField stretch label="Add condition">
																		<Grid gridDefinition={groupConditions.length ? [{ colspan: 2 }, { colspan: 4 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }] : [{ colspan: 4 }, { colspan: 3 }, { colspan: 3 }, { colspan: 2 }]}>
																			{groupConditions.length > 0 && <Select selectedOption={selectedConditionLogicalOperator} placeholder="AND/OR" options={logicalOperatorOptions} onChange={({ detail }) => this.updateNewCondition(group.groupId, { logicalOperator: detail.selectedOption.value })} />}

																			<Select
																				selectedOption={selectedFieldOption}
																				placeholder="Select field"
																				options={fieldOptions}
																				onChange={({ detail }) =>
																					this.updateNewCondition(group.groupId, {
																						field: detail.selectedOption.value,
																						fieldLabel: detail.selectedOption.label,
																						conditionalType: detail.selectedOption.type,
																						specialType: detail.selectedOption.specialType,
																						value: null,
																						operator: detail.selectedOption.type === "select" || detail.selectedOption.type === "multiselect" ? "=" : null,
																					})
																				}
																			/>

																			<Select
																				selectedOption={selectedOperatorOption}
																				placeholder="Select operator"
																				disabled={newCondition.conditionalType === "select" || newCondition.conditionalType === "multiselect"}
																				options={[
																					{ value: "=", label: "=" },
																					{ value: ">", label: ">" },
																					{ value: "<", label: "<" },
																					{ value: ">=", label: ">=" },
																					{ value: "<=", label: "<=" },
																					{ value: "IN", label: "IN" },
																				]}
																				onChange={({ detail }) => this.updateNewCondition(group.groupId, { operator: detail.selectedOption.value })}
																			/>

																			<Box>{this.renderComponentFromType(group.groupId, newCondition.conditionalType, newCondition.specialType)}</Box>

																			<Button disabled={isAddDisabled} onClick={() => this.addCohortCondition(group, groupIndex)}>
																				+ Add
																			</Button>
																		</Grid>
																	</FormField>
																}
																items={groupConditions}
																empty="No conditions set"
															/>
														</SpaceBetween>
													</Container>
												);
											})}
										</SpaceBetween>
									),
								},
								{
									label: "Levels",
									id: "levels",
									content:
										this.props.achievementAddModal?.type?.value === "progressive" && !["verify", "email_verified", "phone_verified", "tg_verified"].includes(this.props.achievementAddModal.sourceType?.value) ? (
											<>
												{this.state.levelError && (
													<Alert type="error" dismissible onDismiss={() => this.setState({ levelError: "" })}>
														{this.state.levelError}
													</Alert>
												)}
												<Modal
													visible={!!this.state.levelEditingRow}
													header="Edit Level"
													footer={
														<Box float="right">
															<SpaceBetween direction="horizontal" size="xs">
																<Button variant="link" onClick={this.closeLevelEditModal}>
																	Cancel
																</Button>
																<Button variant="primary" onClick={this.saveLevelEditModal}>
																	Save
																</Button>
															</SpaceBetween>
														</Box>
													}
													onDismiss={this.closeLevelEditModal}>
													<SpaceBetween direction="vertical" size="m">
														{this.state.levelError && <Alert type="error">{this.state.levelError}</Alert>}
														<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
															<FormField label="Level" description="Achievement lvl">
																<Input type="number" placeholder="Level" value={this.state.levelEditing?.level ?? ""} onChange={(e) => this.updateLevelEditing({ level: e.detail.value })} />
															</FormField>
															<FormField label="Count" description="Achievement count">
																<Input type="number" placeholder="Count" value={this.state.levelEditing?.progress_required ?? ""} onChange={(e) => this.updateLevelEditing({ progress_required: e.detail.value })} />
															</FormField>
															<FormField label="Reward type" description="Achievement reward type">
																<Select
																	selectedOption={this.state.levelEditing?.rewardType}
																	onChange={({ detail }) =>
																		this.updateLevelEditing({
																			rewardType: detail.selectedOption,
																			rewardValue: "",
																			freespinsProvider: null,
																			freespinsGame: null,
																			freespinsBetAmount: "",
																			freespinsExpiresAt: "",
																		})
																	}
																	options={[
																		{ value: "money", label: "Money" },
																		{ value: "tickets", label: "Tickets" },
																		{ value: "freespins", label: "Freespins" },
																	]}
																	placeholder="Reward type"
																/>
															</FormField>
															<FormField label="Reward value" description="Achievement reward value">
																<Input type="number" placeholder="Reward value" value={this.state.levelEditing?.rewardValue ?? ""} onChange={(e) => this.updateLevelEditing({ rewardValue: e.detail.value })} />
															</FormField>
														</Grid>

														{(typeValue === "deposit" || typeValue === "withdrawal") && (
															<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
																<FormField label="Amount" description={typeValue === "deposit" ? "Deposit amount" : "Withdrawal amount"}>
																	<Input type="number" placeholder="Enter amount" value={this.state.levelEditing?.amount ?? ""} onChange={(e) => this.updateLevelEditing({ amount: e.detail.value })} />
																</FormField>
																<FormField label={typeValue === "deposit" ? "Deposit type" : "Withdrawal type"} description={typeValue === "deposit" ? "Deposit type" : "Withdrawal type"}>
																	<Select
																		selectedOption={this.state.levelEditing?.triggerType}
																		onChange={({ detail }) => this.updateLevelEditing({ triggerType: detail.selectedOption })}
																		options={[
																			{ value: "crypto", label: "Crypto" },
																			{ value: "fiat", label: "Fiat" },
																			{ value: "both", label: "Both" },
																		]}
																		placeholder="Select type"
																	/>
																</FormField>
															</Grid>
														)}

														{this.state.levelEditing?.rewardType?.value === "freespins" && (
															<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
																<FormField label="Freespins Provider" description="Provider">
																	<Select
																		filteringType="auto"
																		placeholder="Provider"
																		selectedOption={this.state.levelEditing?.freespinsProvider}
																		onChange={({ detail }) => {
																			this.updateLevelEditing({
																				freespinsProvider: detail.selectedOption,
																				freespinsGame: null,
																			});
																			this.loadAvailableLevelGames(detail.selectedOption.type);
																		}}
																		options={[{ value: "endorphina", label: "Endorphina", type: 10 }]}
																	/>
																</FormField>
																<FormField label="Freespins game" description="Game">
																	<Select filteringType="auto" disabled={!this.state.levelEditing?.freespinsProvider} placeholder="Game" selectedOption={this.state.levelEditing?.freespinsGame} options={this.getFilteredLevelGames()} onChange={({ detail }) => this.updateLevelEditing({ freespinsGame: detail.selectedOption })} />
																</FormField>
																<FormField label="Freespins bet amount" description="Bet amount (e.g., 100 for 100.00 RUB)">
																	<Input type="number" placeholder="Bet Amount" value={this.state.levelEditing?.freespinsBetAmount ?? ""} onChange={(e) => this.updateLevelEditing({ freespinsBetAmount: e.detail.value })} />
																</FormField>
																<FormField label="Freespins expires at" description="Expires at (number)">
																	<Input placeholder="Expires At (timestamp/int)" value={this.state.levelEditing?.freespinsExpiresAt ?? ""} onChange={(e) => this.updateLevelEditing({ freespinsExpiresAt: e.detail.value })} />
																</FormField>
															</Grid>
														)}

														{typeValue === "spins" && (
															<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
																<FormField label="Provider" description="Provider">
																	<Select
																		placeholder="Provider"
																		filteringType="auto"
																		selectedOption={this.state.levelEditing?.spinsProvider}
																		onChange={({ detail }) => {
																			this.updateLevelEditing({
																				spinsProvider: detail.selectedOption,
																				spinsGame: null,
																			});
																			this.loadAvailableLevelSpinsGames(detail.selectedOption.type);
																		}}
																		options={this.props.config.game.options.provider}
																	/>
																</FormField>
																<FormField label="Game" description="Game">
																	<Select filteringType="auto" disabled={!this.state.levelEditing?.spinsProvider} placeholder="Game" selectedOption={this.state.levelEditing?.spinsGame} options={this.getFilteredLevelSpinsGames()} onChange={({ detail }) => this.updateLevelEditing({ spinsGame: detail.selectedOption })} />
																</FormField>
																<FormField label="Spins amount more than" description="Spins amount only coops">
																	<Input placeholder="Spins amount (coops)" type="number" value={this.state.levelEditing?.spinsAmount ?? ""} onChange={(e) => this.updateLevelEditing({ spinsAmount: e.detail.value })} />
																</FormField>
															</Grid>
														)}
													</SpaceBetween>
												</Modal>
												<Table
													columnDefinitions={[
														{
															id: "level",
															header: "Level",
															cell: (item) => item.level,
														},
														{
															id: "progress_required",
															header: "Count",
															cell: (item) => item.progress_required,
														},
														{
															id: "rewardType",
															header: "Reward Type",
															cell: (item) => this.getRewardTypeLabel(item.rewardType),
														},
														{
															id: "reward",
															header: "Reward",
															cell: (item) => this.getRewardText(item),
														},
														...(typeValue === "deposit" || typeValue === "withdrawal"
															? [
																	{
																		id: "amount",
																		header: "Amount",
																		cell: (item) => item.amount ?? "N/A",
																	},
																	{
																		id: "trigger_type",
																		header: "Trigger Type",
																		cell: (item) => item.trigger_type || "N/A",
																	},
															  ]
															: []),
														...(typeValue === "spins"
															? [
																	{
																		id: "spins",
																		header: "Spins",
																		cell: (item) => this.getSpinsText(item),
																	},
															  ]
															: []),
														{
															id: "actions",
															header: "Control",
															cell: (item, i) => (
																<SpaceBetween direction="horizontal" size="xs">
																	<Button variant="icon" iconName="edit" onClick={() => this.openLevelEditModal(item)}>
																		Edit
																	</Button>
																	<Button
																		variant="icon"
																		iconName="delete-marker"
																		onClick={() => {
																			const newLvls = (this.props.achievementAddModal.levels || []).filter((level) => !this.isSameLevelRow(level, item));
																			this.props.setAchievementAddModal({
																				...this.props.achievementAddModal,
																				levels: newLvls,
																			});
																			this.setState({ levelError: "" });
																		}}>
																		Remove
																	</Button>
																</SpaceBetween>
															),
														},
													]}
													items={this.props.achievementAddModal.levels || []}
													footer={
														<>
															<FormField stretch>
																<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, ...(this.props.achievementAddModal.sourceType?.value === "deposit" || this.props.achievementAddModal.sourceType?.value === "withdrawal" ? [{ colspan: 6 }, { colspan: 6 }] : []), { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
																	<FormField label="Level" description="Achievement lvl">
																		<Input type="number" placeholder="Level" value={this.state?.newLevel || ""} onChange={(e) => this.setState({ newLevel: e.detail.value })} />
																	</FormField>
																	<FormField label="Count" description="Achievement count">
																		<Input type="number" placeholder="Count" value={this.state?.newProgress || ""} onChange={(e) => this.setState({ newProgress: e.detail.value })} />
																	</FormField>

																	{(this.props.achievementAddModal.sourceType?.value === "deposit" || this.props.achievementAddModal.sourceType?.value === "withdrawal") && (
																		<>
																			<FormField label="Amount" description={this.props.achievementAddModal.sourceType?.value === "deposit" ? "Deposit amount" : "Withdrawal amount"}>
																				<Input type="number" placeholder="Enter amount" value={this.state?.newAmount || ""} onChange={(e) => this.setState({ newAmount: e.detail.value })} />
																			</FormField>
																			<FormField label={this.props.achievementAddModal.sourceType?.value === "deposit" ? "Deposit type" : "Withdrawal type"} description={this.props.achievementAddModal.sourceType?.value === "deposit" ? "Deposit type" : "Withdrawal type"}>
																				<Select
																					selectedOption={this.state?.newTriggerType}
																					onChange={({ detail }) => this.setState({ newTriggerType: detail.selectedOption })}
																					options={[
																						{ value: "crypto", label: "Crypto" },
																						{ value: "fiat", label: "Fiat" },
																						{ value: "both", label: "Both" },
																					]}
																					placeholder="Select type"
																				/>
																			</FormField>
																		</>
																	)}

																	<FormField label="Reward type" description="Achievement reward type">
																		<Select
																			selectedOption={this.state?.newRewardType}
																			onChange={({ detail }) => {
																				this.setState({
																					newRewardType: detail.selectedOption,
																					newRewardValue: "",
																					newProvider: null,
																					newGame: null,
																					newBetAmount: "",
																					newExpiresAt: "",
																					newChestRewards: [],
																					newChestType: "",
																					newChestPrizeId: null,
																					newChestPrizeSelected: null,
																					availableLevelGames: [],
																					newSpinsProvider: null,
																					newSpinsGame: null,
																					availableLevelSpinsGames: [],
																				});

																				if (detail.selectedOption.value === "chest") {
																					this.loadChestPrizeOptions();
																				}
																			}}
																			options={[
																				{ value: "money", label: "Money" },
																				{ value: "tickets", label: "Tickets" },
																				{ value: "freespins", label: "Freespins" },
																				{ value: "chest", label: "Chest" },
																			]}
																			placeholder="Reward type"
																		/>
																	</FormField>

																	{(this.state?.newRewardType?.value === "money" || this.state?.newRewardType?.value === "tickets") && (
																		<FormField label="Reward value" description="Achievement reward value">
																			<Input type="number" placeholder="Reward value" value={this.state?.newRewardValue || ""} onChange={(e) => this.setState({ newRewardValue: e.detail.value })} />
																		</FormField>
																	)}

																	{this.state?.newRewardType?.value === "freespins" && (
																		<>
																			<FormField label="Freespins Provider" description="Provider">
																				<Select
																					filteringType="auto"
																					placeholder="Provider"
																					selectedOption={this.state?.newProvider}
																					onChange={({ detail }) => {
																						this.setState({
																							newProvider: detail.selectedOption,
																							newGame: null,
																							availableLevelGames: [],
																						});
																						this.loadAvailableLevelGames(detail.selectedOption.type);
																					}}
																					options={[{ value: "endorphina", label: "Endorphina", type: 10 }]}
																				/>
																			</FormField>
																			<FormField label="Freespins game" description="Game">
																				<Select filteringType="auto" disabled={!this.state?.newProvider} placeholder="Game" selectedOption={this.state?.newGame} options={this.getFilteredLevelGames()} onChange={({ detail }) => this.setState({ newGame: detail.selectedOption })} />
																			</FormField>
																			<FormField label="Freespins bet amount" description="Bet amount (e.g., 100 for 100.00 RUB)">
																				<Input type="number" placeholder="Bet Amount" value={this.state?.newBetAmount || ""} onChange={(e) => this.setState({ newBetAmount: e.detail.value })} />
																			</FormField>
																			<FormField label="Freespins expires at" description="Expires at (number)">
																				<Input placeholder="Expires At (timestamp/int)" value={this.state?.newExpiresAt || ""} onChange={(e) => this.setState({ newExpiresAt: e.detail.value })} />
																			</FormField>
																			<FormField label="Freespins reward value" description="Freespins count">
																				<Input type="number" placeholder="Reward Value (spins 1-1000)" value={this.state?.newRewardValue || ""} onChange={(e) => this.setState({ newRewardValue: e.detail.value })} />
																			</FormField>
																		</>
																	)}

																	{typeValue === "spins" && (
																		<>
																			<FormField label="Provider" description="Provider">
																				<Select
																					placeholder="Provider"
																					filteringType="auto"
																					selectedOption={this.state?.newSpinsProvider}
																					onChange={({ detail }) => {
																						this.setState({
																							newSpinsProvider: detail.selectedOption,
																							newSpinsGame: null,
																							availableLevelSpinsGames: [],
																						});
																						this.loadAvailableLevelSpinsGames(detail.selectedOption.type);
																					}}
																					options={this.props.config.game.options.provider}
																				/>
																			</FormField>
																			<FormField label="Game" description="Game">
																				<Select filteringType="auto" disabled={!this.state?.newSpinsProvider} placeholder="Game" selectedOption={this.state?.newSpinsGame} options={this.getFilteredLevelSpinsGames()} onChange={({ detail }) => this.setState({ newSpinsGame: detail.selectedOption })} />
																			</FormField>

																			<FormField label="Spins amount more than" description="Spins amount only coops" stretch>
																				<Input placeholder="Spins amount (coops)" type="number" value={this.state?.newSpinsAmount} onChange={(e) => this.setState({ newSpinsAmount: e.detail.value })} />
																			</FormField>
																		</>
																	)}

																	<FormField label="Add lvl" description="Add lvl">
																		<Button
																			onClick={() => {
																				const err = this.validateNewLevel();
																				if (err) {
																					this.setState({ levelError: err });
																					return;
																				}

																				const rewardType = this.state?.newRewardType?.value;
																				const levelObj = {
																					_rowId: crypto.randomUUID(),
																					level: Number(this.state.newLevel),
																					progress_required: Number(this.state.newProgress) || 0,
																					...(typeValue === "deposit" || typeValue === "withdrawal"
																						? {
																								amount: Number(this.state.newAmount) || 0,
																								trigger_type: this.state.newTriggerType?.value || "both",
																						  }
																						: {}),
																					...(typeValue === "spins"
																						? {
																								gameId: this.state?.newSpinsGame?.gameId,
																								providerId: this.state.newSpinsProvider?.type,
																								amount: Number(this.state.newSpinsAmount || 0) || 0,
																						  }
																						: {}),
																				};

																				if (rewardType === "freespins") {
																					levelObj.rewardType = "freespins";
																					levelObj.rewardValue = Number(this.state.newRewardValue) || 0;
																					levelObj.freespins = {
																						providerId: this.state.newGame?.game.providerID,
																						gameId: this.state.newGame?.game.id,
																						betAmount: Number(this.state.newBetAmount) || 0,
																						expiresAt: parseInt(this.state.newExpiresAt) || 0,
																						currency: "RUB",
																					};
																				} else if (rewardType === "money") {
																					levelObj.rewardType = "money";
																					levelObj.rewardValue = Number(this.state.newRewardValue) || 0;
																				} else if (rewardType === "tickets") {
																					levelObj.rewardType = "tickets";
																					levelObj.rewardValue = Number(this.state.newRewardValue) || 0;
																					levelObj.tickets_reward = Number(this.state.newRewardValue) || 0;
																				} else if (rewardType === "chest") {
																					levelObj.rewardType = "chest";
																					levelObj.rewardValue = 1;
																					levelObj.rewardConditions = {
																						chestPrizeId: Number(this.state.newChestPrizeId),
																					};
																				}

																				this.props.setAchievementAddModal({
																					...this.props.achievementAddModal,
																					levels: [...(this.props.achievementAddModal.levels || []), levelObj],
																				});

																				this.setState({
																					newLevel: "",
																					newProgress: "",
																					newAmount: "",
																					newTriggerType: undefined,
																					newRewardType: undefined,
																					newRewardValue: "",
																					newProvider: null,
																					newGame: null,
																					newBetAmount: "",
																					newExpiresAt: "",
																					newChestRewards: [],
																					newChestType: "",
																					newChestPrizeId: null,
																					newChestPrizeSelected: null,
																					availableLevelGames: [],
																					newSpinsProvider: null,
																					newSpinsGame: null,
																					newSpinsAmount: "",
																					availableLevelSpinsGames: [],
																					levelError: "",
																				});
																			}}>
																			+ Add Level
																		</Button>
																	</FormField>
																</Grid>

																{this.state?.newRewardType?.value === "chest" && this.renderNewLevelChestConfig()}
															</FormField>
														</>
													}
													empty="No levels yet"
												/>
											</>
										) : (
											<Alert type="info">Levels are available only for progressive achievements or supports type.</Alert>
										),
								},
								{
									label: "Media",
									id: "media",
									content: (
										<SpaceBetween direction="vertical" size="m">
											<FormField label="Image" description="Upload an SVG or PNG image" stretch>
												<input type="file" accept="image/svg+xml,image/png" onChange={this.handleImageChange} />
												{this.props.achievementAddModal.image && (
													<Box margin={{ top: "s" }} textAlign="center">
														<img
															src={this.props.achievementAddModal.image}
															alt="Achievement preview"
															style={{
																maxWidth: "100%",
																maxHeight: "150px",
																padding: "4px",
															}}
														/>
													</Box>
												)}
											</FormField>
											{this.renderJsonImagePreview()}
										</SpaceBetween>
									),
								},
								{
									label: "JSON",
									id: "json",
									content: (
										<SpaceBetween direction="vertical" size="m">
											<Container header={<Box variant="h3">Achievement JSON</Box>}>
												<SpaceBetween direction="vertical" size="m">
													<SpaceBetween direction="horizontal" size="xs">
														<Button onClick={this.copyAchievementJson}>Copy JSON</Button>
														<Button variant="primary" onClick={this.applyAchievementJson}>
															Apply JSON
														</Button>
													</SpaceBetween>
													{this.renderJsonImagePreview()}
													{this.state.jsonError && (
														<Alert type="error" dismissible onDismiss={() => this.setState({ jsonError: "" })}>
															{this.state.jsonError}
														</Alert>
													)}
													<FormField label="JSON" stretch>
														<Textarea value={this.getJsonTextareaValue()} onChange={({ detail }) => this.setState({ jsonText: detail.value, jsonError: "", jsonDirty: true })} placeholder="Paste achievement JSON here" rows={18} />
													</FormField>
												</SpaceBetween>
											</Container>
										</SpaceBetween>
									),
								},
								...(this.props.achievementAddModal?.type?.value === "daily_progressive" ? [{
								label: "Daily Progressive",
								id: "daily_progressive",
								content: (
									<DailyProgressiveTab
										config={this.state.dailyProgressiveConfig}
										newMilestone={this.state.newMilestone}
										providerOptions={this.props.config.game.options.provider}
										spinsGamesOptions={this.getFilteredDailyConditionsSpinsGames()}
										milestoneGamesOptions={this.getFilteredMilestoneGames()}
										chestPrizeOptions={this.props.achievementAddModal.chestPrizeOptions}
										chestPrizeLoading={this.props.achievementAddModal.chestPrizeLoading}
										onConfigChange={this.updateDailyProgressiveConfig}
										onDailyConditionsChange={this.updateDailyConditions}
										onDepositConditionsChange={this.updateDailyConditionsDeposit}
										onSpinsConditionsChange={this.updateDailyConditionsSpins}
										onLoadSpinsGames={(provider) => {
											if (provider) {
												this.loadDailyConditionsSpinsGames(provider);
											} else {
												this.setState({ dailyConditionsSpinsGames: [] });
											}
										}}
										onNewMilestoneChange={this.updateNewMilestone}
										onAddMilestone={this.addMilestone}
										onRemoveMilestone={this.removeMilestone}
										onLoadMilestoneGames={this.loadAvailableMilestoneGames}
										onSelectChestPrize={this.selectDailyMilestoneChestPrize}
										onLoadChestPrizeOptions={this.loadChestPrizeOptions}
									/>
								),
							}] : []),
							]}
						/>
					</Form>
				) : (
					<Box textAlign="center">
						<Spinner size="large" />
					</Box>
				)}
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const achievementAddModal = useRecoilValue(achievementAddModalStore);
		const setAchievementAddModal = useSetRecoilState(achievementAddModalStore);
		const game = useRecoilValue(gameStore);
		const config = useRecoilValue(configStore);
		return <Component {...props} config={config} game={game} achievementAddModal={achievementAddModal} setAchievementAddModal={setAchievementAddModal} />;
	};
}

export default withRecoil(AchievementAddModal);
